import React, { useState, useEffect } from 'react';
import {
  collection,
  addDoc,
  Timestamp,
  doc,
  getDoc,
  updateDoc,
  getDocs,
  query,
  where,
  onSnapshot  // ADD THIS
} from 'firebase/firestore';
import {
  PlusIcon,
  XMarkIcon,

} from '@heroicons/react/24/outline';
import { db } from '../../services/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import {
  getProducts,
  updateProductStockForSale,
  validateBulkStockAvailability,
  updateProductSalesTracking
} from '../../services/inventoryService';
import { createInvoice } from '../../services/billingService';
import { printInvoice } from '../../utils/printUtils';
import { createSupplierOrderNotification } from '../../services/supplierNotificationService';

// Customer Management Modal Component
const CustomerModal = ({ isOpen, onClose, onSave, editingCustomer = null }) => {
  const { vendorProfile } = useAuth();
  const { showAlert } = useNotification();
  const [loading, setLoading] = useState(false);
  const [customerData, setCustomerData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    gstNumber: ''
  });

  useEffect(() => {
    if (editingCustomer) {
      setCustomerData(editingCustomer);
    } else {
      setCustomerData({
        name: '',
        email: '',
        phone: '',
        address: '',
        gstNumber: ''
      });
    }
  }, [editingCustomer]);

  const handleInputChange = (field, value) => {
    setCustomerData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!customerData.name.trim()) {
      showAlert('Customer name is required', 'error');
      return;
    }

    setLoading(true);
    try {
      const customerDoc = {
        ...customerData,
        vendorId: vendorProfile.uid,
        createdAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date())
      };

      if (editingCustomer && editingCustomer.id) {
        // Update existing customer
        await updateDoc(doc(db, 'customers', editingCustomer.id), {
          ...customerDoc,
          createdAt: editingCustomer.createdAt // Keep original creation date
        });
        showAlert('Customer updated successfully', 'success');
      } else {
        // Add new customer
        const docRef = await addDoc(collection(db, 'customers'), customerDoc);
        customerDoc.id = docRef.id;
        showAlert('Customer added successfully', 'success');
      }

      onSave(customerDoc);
      onClose();
    } catch (error) {
      console.error('Error saving customer:', error);
      showAlert('Failed to save customer', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity z-50" onClick={onClose}></div>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
            <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                  <h3 className="text-lg font-semibold leading-6 text-gray-900 mb-4">
                    {editingCustomer ? 'Edit Customer' : 'Add New Customer'}
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Customer Name *</label>
                      <input
                        type="text"
                        required
                        value={customerData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                        placeholder="Enter customer name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Email</label>
                      <input
                        type="email"
                        value={customerData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                        placeholder="customer@example.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Phone</label>
                      <input
                        type="tel"
                        value={customerData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                        placeholder="+91 9876543210"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">GST Number</label>
                      <input
                        type="text"
                        value={customerData.gstNumber}
                        onChange={(e) => handleInputChange('gstNumber', e.target.value)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                        placeholder="Enter GST number"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Address</label>
                      <textarea
                        rows={3}
                        value={customerData.address}
                        onChange={(e) => handleInputChange('address', e.target.value)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                        placeholder="Customer address"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
              <button
                type="button"
                onClick={handleSave}
                disabled={loading}
                className="inline-flex w-full justify-center rounded-md bg-orange-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-orange-500 disabled:opacity-50 sm:ml-3 sm:w-auto"
              >
                {loading ? 'Saving...' : (editingCustomer ? 'Update' : 'Add')} Customer
              </button>
              <button
                type="button"
                onClick={onClose}
                className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// Enhanced Product Selection Modal Component
// Enhanced Product Selection Modal Component
// Enhanced Product Selection Modal Component
const ProductSelectionModal = ({
  isOpen,
  onClose,
  products,
  onSelectProducts,
  selectedProducts = [],
  onAddManualItem,
  onNavigateToAddProduct,
  brands = [] // Add brands prop
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredProducts, setFilteredProducts] = useState(products);
  const [tempSelectedProducts, setTempSelectedProducts] = useState([...selectedProducts]);

  // Helper functions from InventoryTable
  const getBrandLogoUrl = (productBrand) => {
    if (!productBrand) {
      return `data:image/svg+xml;base64,${btoa(`
        <svg width="80" height="48" xmlns="http://www.w3.org/2000/svg">
          <rect width="80" height="48" fill="#E5E7EB"/>
          <text x="40" y="28" text-anchor="middle" font-family="Arial" font-size="10" fill="#6B7280">No Brand</text>
        </svg>
      `)}`;
    }

    if (!brands || brands.length === 0) {
      const brandName = typeof productBrand === 'string' ? productBrand : 'Brand';
      return `data:image/svg+xml;base64,${btoa(`
        <svg width="80" height="48" xmlns="http://www.w3.org/2000/svg">
          <rect width="80" height="48" fill="#F3F4F6"/>
          <text x="40" y="28" text-anchor="middle" font-family="Arial" font-size="10" fill="#374151">${brandName}</text>
        </svg>
      `)}`;
    }

    let brandObj = null;

    if (typeof productBrand === 'string') {
      brandObj = brands.find(b => b.id === productBrand) ||
        brands.find(b => b.name && b.name.toLowerCase() === productBrand.toLowerCase());
    } else if (Array.isArray(productBrand) && productBrand.length > 0) {
      const firstBrand = productBrand[0];
      brandObj = brands.find(b => b.id === firstBrand || (b.name && b.name.toLowerCase() === firstBrand.toLowerCase()));
    } else if (productBrand && typeof productBrand === 'object') {
      brandObj = productBrand;
    }

    if (brandObj && brandObj.logo) {
      return brandObj.logo;
    }

    const brandName = brandObj?.name || (typeof productBrand === 'string' ? productBrand : 'Brand');
    return `data:image/svg+xml;base64,${btoa(`
      <svg width="80" height="48" xmlns="http://www.w3.org/2000/svg">
        <rect width="80" height="48" fill="#F9FAFB" stroke="#D1D5DB"/>
        <text x="40" y="28" text-anchor="middle" font-family="Arial" font-size="9" fill="#374151">${brandName}</text>
      </svg>
    `)}`;
  };

  const getBrandName = (productBrand) => {
    if (!productBrand) return 'N/A';

    if (!brands || brands.length === 0) {
      if (typeof productBrand === 'string') return productBrand;
      if (Array.isArray(productBrand) && productBrand.length > 0) return productBrand[0];
      if (productBrand && typeof productBrand === 'object') return productBrand.name || 'N/A';
      return 'N/A';
    }

    if (typeof productBrand === 'string') {
      const brandObj = brands.find(b => b.id === productBrand || (b.name && b.name.toLowerCase() === productBrand.toLowerCase()));
      return brandObj?.name || productBrand;
    } else if (Array.isArray(productBrand) && productBrand.length > 0) {
      const firstBrand = productBrand[0];
      const brandObj = brands.find(b => b.id === firstBrand || (b.name && b.name.toLowerCase() === firstBrand.toLowerCase()));
      return brandObj?.name || firstBrand;
    } else if (productBrand && typeof productBrand === 'object') {
      return productBrand.name || 'N/A';
    }

    return 'N/A';
  };

  const getStockBadge = (stock, reorderLevel = 10) => {
    const stockNum = Number(stock) || 0;
    const reorderNum = Number(reorderLevel) || 10;

    if (stockNum <= 0) {
      return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">Out of Stock</span>;
    } else if (stockNum <= reorderNum) {
      return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Low: {stockNum}</span>;
    }
    return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">{stockNum}</span>;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getDisplayImageUrl = (product) => {
    if (product.imageUrls && Array.isArray(product.imageUrls) && product.imageUrls.length > 0) {
      const validUrls = product.imageUrls.filter(url => url && typeof url === 'string');
      if (validUrls.length > 0) return validUrls[0];
    }
    return null;
  };

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredProducts(products);
    } else {
      const filtered = products.filter(product =>
        product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (product.partNumber && product.partNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (product.sku && product.sku.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (product.description && product.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (getBrandName(product.brand || product.brands).toLowerCase().includes(searchTerm.toLowerCase())) ||
        (product.categories && Array.isArray(product.categories) && product.categories.some(cat =>
          (typeof cat === 'string' && cat.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (cat.name && cat.name.toLowerCase().includes(searchTerm.toLowerCase()))
        ))
      );
      setFilteredProducts(filtered);
    }
  }, [searchTerm, products, brands]);

  const handleQuantityChange = (productId, quantity) => {
    // Allow empty string for clearing, but use 1 as minimum when empty
    const parsedQuantity = quantity === '' ? '' : Math.max(1, parseInt(quantity) || 1);

    setTempSelectedProducts(prev => {
      const existing = prev.find(item => item.productId === productId);
      if (existing) {
        return prev.map(item =>
          item.productId === productId ? { ...item, quantity: parsedQuantity } : item
        );
      }
      const product = products.find(p => p.id === productId);
      return [...prev, {
        productId,
        name: product.name,
        quantity: parsedQuantity === '' ? 1 : parsedQuantity,
        rate: product.price || product.sellingPrice || 0,
        hsnCode: product.hsnCode || '',
        gstRate: 18 // Default GST rate
      }];
    });
  };

  const handleToggleProduct = (product) => {
    setTempSelectedProducts(prev => {
      const existing = prev.find(item => item.productId === product.id);
      if (existing) {
        return prev.filter(item => item.productId !== product.id);
      }
      return [...prev, {
        productId: product.id,
        name: product.name,
        quantity: 1,
        rate: product.price || product.sellingPrice || 0,
        hsnCode: product.hsnCode || '',
        gstRate: 18 // Default GST rate
      }];
    });
  };

  const handleConfirm = () => {
    onSelectProducts(tempSelectedProducts);
    onClose();
  };

  const handleAddManualItemClick = () => {
    onAddManualItem();
    onClose();
  };

  const handleAddNewProductClick = () => {
    onNavigateToAddProduct();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity z-40"
        onClick={onClose}
      ></div>
      <div className="fixed inset-y-0 right-0 w-[480px] bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out">
        <div className="h-full flex flex-col">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Select Products</h3>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Action Buttons */}
            <div className="mt-4 flex space-x-2">
              <button
                type="button"
                onClick={handleAddManualItemClick}
                className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Manual Item
              </button>
              <button
                type="button"
                onClick={handleAddNewProductClick}
                className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
              >
                <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add New Product
              </button>
            </div>

            <div className="mt-4">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                  placeholder="Search products by name, part number, brand..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {filteredProducts.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                {searchTerm ? 'No products found matching your search.' : 'No products available.'}
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {filteredProducts.map((product) => (
                  <div
                    key={product.id}
                    className={`p-4 cursor-pointer transition-colors ${tempSelectedProducts.some(item => item.productId === product.id)
                      ? 'bg-red-50 border-l-4 border-red-500'
                      : 'hover:bg-gray-50'
                      }`}
                    onClick={() => handleToggleProduct(product)}
                  >
                    <div className="flex items-start space-x-3">
                      {/* Product Image */}
                      <div className="flex-shrink-0 h-16 w-16 relative">
                        {getDisplayImageUrl(product) ? (
                          <>
                            <img
                              className="h-16 w-16 rounded-md object-cover border border-gray-200"
                              src={getDisplayImageUrl(product)}
                              alt={product.name}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextElementSibling.style.display = 'flex';
                              }}
                            />
                            <div className={`h-16 w-16 rounded-md bg-gray-200 items-center justify-center text-gray-500 border border-gray-200 hidden absolute inset-0`}>
                              <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                          </>
                        ) : (
                          <div className="h-16 w-16 rounded-md bg-gray-200 flex items-center justify-center text-gray-500 border border-gray-200">
                            <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                        )}
                      </div>

                      {/* Product Details */}
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-gray-900 truncate" title={product.name}>
                          {product.name || 'Unnamed Product'}
                        </h4>

                        {/* Part Number and SKU */}
                        <div className="text-sm text-gray-500 mt-1">
                          {product.partNumber && `Part: ${product.partNumber}`}
                          {product.partNumber && product.sku && ' • '}
                          {product.sku && `SKU: ${product.sku}`}
                        </div>

                        {/* Brand Name Only */}
                        {(product.brand || product.brands) && (
                          <div className="text-xs text-gray-600 mt-1">
                            Brand: {getBrandName(product.brand || product.brands)}
                          </div>
                        )}

                        {/* Stock and Price */}
                        <div className="mt-2 flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            {getStockBadge(product.stock || 0, product.reorderLevel)}
                            <span className="text-sm font-medium text-gray-900">
                              {formatCurrency(product.price || product.sellingPrice || 0)}
                            </span>
                          </div>
                        </div>

                        {/* Quantity Input for Selected Products */}
                        {tempSelectedProducts.some(item => item.productId === product.id) && (
                          <div className="mt-3 flex items-center space-x-2">
                            <label className="text-xs text-gray-600 font-medium">Quantity:</label>
                            <input
                              type="number"
                              min="1"
                              max={product.stock || 999}
                              value={tempSelectedProducts.find(item => item.productId === product.id)?.quantity || ''}
                              onChange={(e) => handleQuantityChange(product.id, e.target.value)}
                              onBlur={(e) => {
                                // If empty on blur, set to 1
                                if (e.target.value === '' || e.target.value === '0') {
                                  handleQuantityChange(product.id, '1');
                                }
                              }}
                              className="w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-red-500"
                              onClick={(e) => e.stopPropagation()}
                              placeholder="1"
                            />
                            <span className="text-xs text-gray-500">
                              of {product.stock || 0} available
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Selection Indicator */}
                      <div className="flex-shrink-0">
                        {tempSelectedProducts.some(item => item.productId === product.id) ? (
                          <div className="text-red-600">
                            <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          </div>
                        ) : (
                          <div className="text-gray-300">
                            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer with Confirm Button */}
          <div className="border-t border-gray-200 px-6 py-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-600">
                {tempSelectedProducts.length} product{tempSelectedProducts.length !== 1 ? 's' : ''} selected
              </span>
              {tempSelectedProducts.length > 0 && (
                <button
                  type="button"
                  onClick={() => setTempSelectedProducts([])}
                  className="text-sm text-red-600 hover:text-red-800 font-medium"
                >
                  Clear All
                </button>
              )}
            </div>
            <button
              type="button"
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleConfirm}
              disabled={tempSelectedProducts.length === 0}
            >
              Confirm Selection ({tempSelectedProducts.length})
            </button>
          </div>
        </div>
      </div>
    </>
  );
};



// ==================== SUPPLIER MODAL (for Pickup) ====================
const SupplierModal = ({ isOpen, onClose, onSave, vendorId }) => {
  const { showAlert } = useNotification();
  const [loading, setLoading] = useState(false);
  const [supplierData, setSupplierData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    landmark: '',
    latitude: '',
    longitude: '',
    gstNumber: '',
    type: 'supplier'
  });

  useEffect(() => {
    if (isOpen) {
      setSupplierData({
        name: '',
        email: '',
        phone: '',
        address: '',
        landmark: '',
        latitude: '',
        longitude: '',
        gstNumber: '',
        type: 'supplier'
      });
    }
  }, [isOpen]);

  const handleSave = async () => {
    if (!supplierData.name.trim()) {
      showAlert('Name is required', 'error');
      return;
    }

    setLoading(true);
    try {
      const supplierDoc = {
        ...supplierData,
        latitude: supplierData.latitude ? parseFloat(supplierData.latitude) : null,
        longitude: supplierData.longitude ? parseFloat(supplierData.longitude) : null,
        vendorId: vendorId,
        createdAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date())
      };

      const docRef = await addDoc(collection(db, 'customers'), supplierDoc);
      supplierDoc.id = docRef.id;
      showAlert('Supplier added successfully', 'success');

      onSave(supplierDoc);
      onClose();
    } catch (error) {
      console.error('Error saving supplier:', error);
      showAlert('Failed to save supplier', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Add Supplier/Pickup Contact</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">Name *</label>
            <input
              type="text"
              value={supplierData.name}
              onChange={(e) => setSupplierData({ ...supplierData, name: e.target.value })}
              placeholder="Enter name"
              className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">Phone</label>
              <input
                type="tel"
                value={supplierData.phone}
                onChange={(e) => setSupplierData({ ...supplierData, phone: e.target.value })}
                placeholder="+91 98765 43210"
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">Email</label>
              <input
                type="email"
                value={supplierData.email}
                onChange={(e) => setSupplierData({ ...supplierData, email: e.target.value })}
                placeholder="email@example.com"
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">Address</label>
            <textarea
              rows={2}
              value={supplierData.address}
              onChange={(e) => setSupplierData({ ...supplierData, address: e.target.value })}
              placeholder="Full address"
              className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">Landmark</label>
              <input
                type="text"
                value={supplierData.landmark}
                onChange={(e) => setSupplierData({ ...supplierData, landmark: e.target.value })}
                placeholder="Near..."
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">Latitude</label>
              <input
                type="text"
                value={supplierData.latitude}
                onChange={(e) => setSupplierData({ ...supplierData, latitude: e.target.value })}
                placeholder="e.g., 12.9716"
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">Longitude</label>
              <input
                type="text"
                value={supplierData.longitude}
                onChange={(e) => setSupplierData({ ...supplierData, longitude: e.target.value })}
                placeholder="e.g., 77.5946"
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
          </div>
        </div>

        <div className="flex gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="flex-1 py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-xl transition-colors disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Add Supplier'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== PICKUP LOCATION CARD (Online Mode) ====================
const PickupLocationCard = ({
  pickup,
  index,
  onUpdate,
  onRemove,
  onAddProducts,
  onRemoveProduct,
  onUpdateProductQty,
  canRemove,
  suppliers,
  onAddSupplier
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);

  const totalAmount = pickup.products.reduce((sum, p) => sum + ((p.rate || p.price || 0) * (p.quantity || 1)), 0);
  const totalItems = pickup.products.reduce((sum, p) => sum + (p.quantity || 1), 0);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const handleContactSearch = (value) => {
    onUpdate({ ...pickup, contactName: value });

    if (value.trim()) {
      const filtered = suppliers.filter(s =>
        s.name?.toLowerCase().includes(value.toLowerCase()) ||
        (s.phone && s.phone.includes(value))
      );
      setFilteredSuppliers(filtered);
      setShowSupplierDropdown(true);
    } else {
      setShowSupplierDropdown(false);
    }
  };

  const handleSupplierSelect = (supplier) => {
    onUpdate({
      ...pickup,
      contactName: supplier.name,
      contactPhone: supplier.phone || '',
      address: supplier.address || pickup.address,
      landmark: supplier.landmark || '',
      latitude: supplier.latitude?.toString() || '',
      longitude: supplier.longitude?.toString() || '',
    });
    setShowSupplierDropdown(false);
  };

  return (
    <div className="bg-white border-2 border-green-200 rounded-2xl overflow-hidden shadow-sm">
      {/* Header */}
      <div
        className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-600 rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div>
              <h4 className="text-gray-900 font-semibold">Pickup Location #{index + 1}</h4>
              <p className="text-gray-500 text-sm">
                {pickup.products.length} product{pickup.products.length !== 1 ? 's' : ''} • {totalItems} item{totalItems !== 1 ? 's' : ''} • {formatCurrency(totalAmount)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canRemove && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onRemove(); }}
                className="p-2 text-red-500 hover:bg-red-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
            <svg className={`w-5 h-5 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {/* Content */}
      {isExpanded && (
        <div className="p-4 space-y-4">
          {/* Contact Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-gray-700 text-sm font-medium">Contact Name</label>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onAddSupplier(); }}
                  className="text-xs text-green-600 hover:text-green-700 font-medium flex items-center gap-1"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add New
                </button>
              </div>
              <input
                type="text"
                value={pickup.contactName}
                onChange={(e) => handleContactSearch(e.target.value)}
                onFocus={() => {
                  if (pickup.contactName?.trim() && filteredSuppliers.length > 0) {
                    setShowSupplierDropdown(true);
                  }
                }}
                onBlur={() => setTimeout(() => setShowSupplierDropdown(false), 200)}
                placeholder="Search or enter contact name"
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500"
              />

              {showSupplierDropdown && filteredSuppliers.length > 0 && (
                <div className="absolute z-20 mt-1 w-full bg-white shadow-lg max-h-48 rounded-xl border border-gray-200 overflow-auto">
                  {filteredSuppliers.map((supplier) => (
                    <div
                      key={supplier.id}
                      className="cursor-pointer py-3 px-4 hover:bg-green-50 border-b border-gray-100 last:border-0"
                      onClick={() => handleSupplierSelect(supplier)}
                    >
                      <p className="font-medium text-gray-900">{supplier.name}</p>
                      <p className="text-gray-500 text-sm">
                        {supplier.phone && <span>{supplier.phone}</span>}
                        {supplier.address && <span className="text-xs"> • {supplier.address.substring(0, 30)}...</span>}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">Contact Phone</label>
              <input
                type="tel"
                value={pickup.contactPhone}
                onChange={(e) => onUpdate({ ...pickup, contactPhone: e.target.value })}
                placeholder="+91..."
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
          </div>

          {/* Address */}
          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">Pickup Address *</label>
            <textarea
              value={pickup.address}
              onChange={(e) => onUpdate({ ...pickup, address: e.target.value })}
              placeholder="Full pickup address"
              rows={2}
              className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
            />
          </div>

          {/* Landmark and Coordinates */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">Landmark</label>
              <input
                type="text"
                value={pickup.landmark}
                onChange={(e) => onUpdate({ ...pickup, landmark: e.target.value })}
                placeholder="Near..."
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">Latitude</label>
              <input
                type="text"
                value={pickup.latitude}
                onChange={(e) => onUpdate({ ...pickup, latitude: e.target.value })}
                placeholder="e.g., 12.9716"
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">Longitude</label>
              <input
                type="text"
                value={pickup.longitude}
                onChange={(e) => onUpdate({ ...pickup, longitude: e.target.value })}
                placeholder="e.g., 77.5946"
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
          </div>

          {/* Products */}
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h5 className="text-gray-900 font-medium">Products to pickup</h5>
              <button
                type="button"
                onClick={onAddProducts}
                className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add Products
              </button>
            </div>

            {pickup.products.length > 0 ? (
              <div className="space-y-2">
                {pickup.products.map((product, pIdx) => (
                  <div key={product.productId || pIdx} className="flex items-center gap-3 bg-white p-3 rounded-lg border border-gray-200">
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {product.imageUrl ? (
                        <img src={product.imageUrl} alt={product.name} className="w-full h-full object-cover rounded-lg" />
                      ) : (
                        <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-900 font-medium text-sm truncate">{product.name}</p>
                      <p className="text-gray-500 text-xs">{formatCurrency(product.rate || product.price || 0)} × {product.quantity || 1}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => onUpdateProductQty(pIdx, (product.quantity || 1) - 1)}
                        className="w-6 h-6 bg-gray-200 hover:bg-gray-300 rounded text-gray-700 text-sm font-bold"
                      >
                        -
                      </button>
                      <span className="w-8 text-center text-sm font-medium">{product.quantity || 1}</span>
                      <button
                        type="button"
                        onClick={() => onUpdateProductQty(pIdx, (product.quantity || 1) + 1)}
                        className="w-6 h-6 bg-gray-200 hover:bg-gray-300 rounded text-gray-700 text-sm font-bold"
                      >
                        +
                      </button>
                    </div>
                    <span className="text-orange-600 font-bold text-sm min-w-[60px] text-right">
                      {formatCurrency((product.rate || product.price || 0) * (product.quantity || 1))}
                    </span>
                    <button
                      type="button"
                      onClick={() => onRemoveProduct(pIdx)}
                      className="p-1 text-red-500 hover:bg-red-100 rounded transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}

                <div className="flex justify-end pt-2 border-t border-gray-200 mt-2">
                  <div className="text-right">
                    <span className="text-gray-500 text-sm">Pickup Total: </span>
                    <span className="text-lg font-bold text-green-600">{formatCurrency(totalAmount)}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-6 border-2 border-dashed border-gray-300 rounded-lg">
                <svg className="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
                <p className="text-gray-500 text-sm">No products added</p>
                <button
                  type="button"
                  onClick={onAddProducts}
                  className="text-green-600 hover:text-green-700 text-sm font-medium mt-1"
                >
                  Add products for this pickup
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};



// ==================== ADD SUPPLIER INLINE FORM (for Online Mode) ====================
// ==================== ADD SUPPLIER INLINE FORM ====================
function AddSupplierInlineForm({ onSave, onCancel, vendorId }) {
  const [loading, setLoading] = useState(false);
  const [supplierData, setSupplierData] = useState({
    name: '',
    phone: '',
    email: '',  // ADD EMAIL FIELD
    address: '',
    landmark: '',
    latitude: '',
    longitude: '',
    gstNumber: '',
    type: 'supplier'
  });

  const handleSave = async () => {
    if (!supplierData.name.trim()) {
      alert('Supplier name is required');
      return;
    }

    // Email validation
    if (supplierData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(supplierData.email)) {
      alert('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      const supplierDoc = {
        ...supplierData,
        latitude: supplierData.latitude ? parseFloat(supplierData.latitude) : null,
        longitude: supplierData.longitude ? parseFloat(supplierData.longitude) : null,
        vendorId: vendorId,
        createdAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date())
      };

      const docRef = await addDoc(collection(db, 'customers'), supplierDoc);
      supplierDoc.id = docRef.id;
      onSave(supplierDoc);
    } catch (error) {
      console.error('Error saving supplier:', error);
      alert('Failed to save supplier');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-green-50 border-2 border-green-300 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-green-800 font-semibold flex items-center gap-2">
          <PlusIcon className="w-4 h-4" />
          Add New Supplier
        </h4>
        <button type="button" onClick={onCancel} className="text-gray-500 hover:text-gray-700">
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>
      
      <div className="grid grid-cols-2 gap-3">
        <div>
          <input
            type="text"
            value={supplierData.name}
            onChange={(e) => setSupplierData({ ...supplierData, name: e.target.value })}
            placeholder="Supplier Name *"
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
          />
        </div>
        <div>
          <input
            type="tel"
            value={supplierData.phone}
            onChange={(e) => setSupplierData({ ...supplierData, phone: e.target.value })}
            placeholder="Phone Number"
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
          />
        </div>
      </div>

      {/* EMAIL FIELD - NEW */}
      <div>
        <input
          type="email"
          value={supplierData.email}
          onChange={(e) => setSupplierData({ ...supplierData, email: e.target.value })}
          placeholder="Email Address (for order notifications)"
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
        />
        <p className="text-xs text-gray-500 mt-1">Supplier will receive order notifications if they have an account with this email</p>
      </div>
      
      <div>
        <input
          type="text"
          value={supplierData.address}
          onChange={(e) => setSupplierData({ ...supplierData, address: e.target.value })}
          placeholder="Address"
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
        />
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div>
          <input
            type="text"
            value={supplierData.landmark}
            onChange={(e) => setSupplierData({ ...supplierData, landmark: e.target.value })}
            placeholder="Landmark"
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
          />
        </div>
        <div>
          <input
            type="text"
            value={supplierData.latitude}
            onChange={(e) => setSupplierData({ ...supplierData, latitude: e.target.value })}
            placeholder="Latitude"
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
          />
        </div>
        <div>
          <input
            type="text"
            value={supplierData.longitude}
            onChange={(e) => setSupplierData({ ...supplierData, longitude: e.target.value })}
            placeholder="Longitude"
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
          />
        </div>
      </div>
      
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors text-sm"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={loading || !supplierData.name.trim()}
          className="flex-1 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 text-sm"
        >
          {loading ? 'Saving...' : 'Add Supplier'}
        </button>
      </div>
    </div>
  );
}


// ==================== ONLINE PRODUCT SELECTION MODAL WITH SUPPLIER ====================
const OnlineProductSelectionModal = ({
  isOpen,
  onClose,
  onSelect,
  selectedProducts,
  products,
  suppliers,
  onSupplierAdded,
  mode = 'add',
  vendorId
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [tempSelected, setTempSelected] = useState([]);
  const [showAddSupplier, setShowAddSupplier] = useState(false);
  const [supplierSearchQuery, setSupplierSearchQuery] = useState('');
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(null);

  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit') {
        setTempSelected([...selectedProducts]);
      } else {
        setTempSelected([]);
      }
      setShowAddSupplier(false);
      setSupplierSearchQuery('');
      setShowSupplierDropdown(null);
      setSearchQuery('');
    }
  }, [isOpen, selectedProducts, mode]);

  const filteredProducts = products.filter(p => {
    const name = p.name?.toLowerCase() || '';
    const partNumber = p.partNumber?.toLowerCase() || '';
    const sku = p.sku?.toLowerCase() || '';
    const q = searchQuery.toLowerCase();
    return name.includes(q) || partNumber.includes(q) || sku.includes(q);
  });

  const filteredSuppliers = suppliers.filter(s => {
    const name = s.name?.toLowerCase() || '';
    const phone = s.phone?.toLowerCase() || '';
    const q = supplierSearchQuery.toLowerCase();
    return name.includes(q) || phone.includes(q);
  });

  const handleToggleProduct = (product) => {
    const tempKey = `temp_${product.id}_${Date.now()}`;
    setTempSelected(prev => [...prev, {
      id: product.id,
      tempKey: tempKey,
      name: product.name,
      partNumber: product.partNumber || product.sku,
      price: product.price || product.sellingPrice || 0,
      quantity: 1,
      imageUrl: product.imageUrls?.[0] || product.imageUrl,
      stock: product.stock || 0,
      hsnCode: product.hsnCode || '',
      gstRate: 18,
      supplierId: null,
      supplierName: '',
      supplierPhone: '',
      supplierAddress: '',
      supplierLandmark: '',
      supplierLatitude: '',
      supplierLongitude: ''
    }]);
  };

  const handleRemoveFromTemp = (tempKey) => {
    setTempSelected(prev => prev.filter(p => p.tempKey !== tempKey));
  };

  const handleQuantityChange = (tempKey, quantity) => {
    setTempSelected(prev => prev.map(p => p.tempKey === tempKey ? { ...p, quantity: Math.max(1, quantity) } : p));
  };

const handleSupplierSelect = (tempKey, supplier) => {
  setTempSelected(prev =>
    prev.map(p => p.tempKey === tempKey ? { 
      ...p, 
      supplierId: supplier.id,
      supplierName: supplier.name,
      supplierPhone: supplier.phone || '',
      supplierEmail: supplier.email || '',  // IMPORTANT: Include email
      supplierAddress: supplier.address || '',
      supplierLandmark: supplier.landmark || '',
      supplierLatitude: supplier.latitude?.toString() || '',
      supplierLongitude: supplier.longitude?.toString() || ''
    } : p)
  );
  setShowSupplierDropdown(null);
  setSupplierSearchQuery('');
};

  const handleNewSupplierAdded = (supplier) => {
    onSupplierAdded(supplier);
    setShowAddSupplier(false);
    if (showSupplierDropdown) {
      handleSupplierSelect(showSupplierDropdown, supplier);
    }
  };

  const handleConfirm = () => {
    const productsWithoutSupplier = tempSelected.filter(p => !p.supplierId);
    if (productsWithoutSupplier.length > 0) {
      alert(`Please assign suppliers to all products. ${productsWithoutSupplier.length} product(s) need a supplier.`);
      return;
    }

    if (mode === 'add') {
      const mergedProducts = [...selectedProducts];
      tempSelected.forEach(newProduct => {
        const existingIndex = mergedProducts.findIndex(p => p.id === newProduct.id && p.supplierId === newProduct.supplierId);
        if (existingIndex >= 0) {
          mergedProducts[existingIndex] = {
            ...mergedProducts[existingIndex],
            quantity: mergedProducts[existingIndex].quantity + newProduct.quantity
          };
        } else {
          mergedProducts.push({
            ...newProduct,
            uniqueKey: `${newProduct.id}_${newProduct.supplierId}_${Date.now()}`
          });
        }
      });
      onSelect(mergedProducts);
    } else {
      onSelect(tempSelected);
    }
    onClose();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {mode === 'edit' ? 'Edit Products & Suppliers' : 'Add Products & Assign Suppliers'}
            </h2>
            <p className="text-gray-500 text-sm mt-1">
              {products.length} products available • Select products and assign suppliers
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <svg className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search products by name or part number..."
              className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-4">
            {/* Selected Products Section */}
            {tempSelected.length > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <h4 className="text-green-800 font-semibold mb-3">✓ Selected Products ({tempSelected.length})</h4>
                <div className="space-y-3">
                  {tempSelected.map((selectedItem) => {
                    const assignedSupplier = selectedItem.supplierId ? suppliers.find(s => s.id === selectedItem.supplierId) : null;

                    return (
                      <div key={selectedItem.tempKey} className="bg-white rounded-lg p-3 border border-green-300">
                        {/* Product Info */}
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                            {selectedItem.imageUrl ? (
                              <img src={selectedItem.imageUrl} alt={selectedItem.name} className="w-full h-full object-cover" />
                            ) : (
                              <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                              </svg>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h5 className="text-gray-900 font-medium text-sm truncate">{selectedItem.name}</h5>
                            <p className="text-gray-500 text-xs">{selectedItem.partNumber || 'No Part #'}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <button type="button" onClick={() => handleQuantityChange(selectedItem.tempKey, selectedItem.quantity - 1)} className="w-7 h-7 bg-gray-100 hover:bg-gray-200 rounded font-bold text-gray-700 text-sm">-</button>
                            <span className="w-8 text-center text-sm font-medium">{selectedItem.quantity}</span>
                            <button type="button" onClick={() => handleQuantityChange(selectedItem.tempKey, selectedItem.quantity + 1)} className="w-7 h-7 bg-gray-100 hover:bg-gray-200 rounded font-bold text-gray-700 text-sm">+</button>
                          </div>
                          <span className="text-green-600 font-bold text-sm min-w-[70px] text-right">
                            {formatCurrency(selectedItem.price * selectedItem.quantity)}
                          </span>
                          <button type="button" onClick={() => handleRemoveFromTemp(selectedItem.tempKey)} className="p-1 text-red-500 hover:bg-red-100 rounded">
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>

                        {/* Supplier Selection */}
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <span className="text-xs font-medium text-gray-600">Pickup Supplier *</span>

                          {assignedSupplier ? (
                            <div
                              className="flex items-center justify-between bg-green-100 rounded-lg p-2 mt-1 cursor-pointer hover:bg-green-200"
                              onClick={() => setShowSupplierDropdown(selectedItem.tempKey)}
                            >
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center">
                                  <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                  </svg>
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900 text-sm">{assignedSupplier.name}</p>
                                  <p className="text-xs text-gray-500">{assignedSupplier.phone || assignedSupplier.address?.substring(0, 25) || ''}</p>
                                </div>
                              </div>
                              <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </div>
                          ) : (
                            <div
                              className="flex items-center justify-between bg-yellow-100 border border-yellow-300 rounded-lg p-2 mt-1 cursor-pointer hover:bg-yellow-200"
                              onClick={() => setShowSupplierDropdown(selectedItem.tempKey)}
                            >
                              <span className="text-yellow-700 text-sm">Select supplier for pickup</span>
                              <svg className="w-4 h-4 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </div>
                          )}

                          {/* Supplier Dropdown */}
                          {showSupplierDropdown === selectedItem.tempKey && (
                            <div className="mt-2 space-y-2">
                              <input
                                type="text"
                                value={supplierSearchQuery}
                                onChange={(e) => setSupplierSearchQuery(e.target.value)}
                                placeholder="Search suppliers..."
                                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500"
                                autoFocus
                              />

                              {showAddSupplier ? (
                                <AddSupplierInlineForm
                                  onSave={handleNewSupplierAdded}
                                  onCancel={() => setShowAddSupplier(false)}
                                  vendorId={vendorId}
                                />
                              ) : (
                                <>
                                  <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg">
                                    {filteredSuppliers.length > 0 ? (
                                      filteredSuppliers.map(supplier => (
                                        <div
                                          key={supplier.id}
                                          className={`p-2 cursor-pointer hover:bg-green-50 border-b border-gray-100 last:border-0 ${selectedItem.supplierId === supplier.id ? 'bg-green-100' : ''}`}
                                          onClick={() => handleSupplierSelect(selectedItem.tempKey, supplier)}
                                        >
                                          <p className="font-medium text-gray-900 text-sm">{supplier.name}</p>
                                          <p className="text-xs text-gray-500">{supplier.phone || supplier.address?.substring(0, 30) || 'No details'}</p>
                                        </div>
                                      ))
                                    ) : (
                                      <div className="p-3 text-center text-gray-500 text-sm">No suppliers found</div>
                                    )}
                                  </div>
                                  <div className="flex justify-between items-center">
                                    <button type="button" onClick={() => setShowAddSupplier(true)} className="text-xs text-green-600 hover:text-green-700 font-medium">
                                      + Add New Supplier
                                    </button>
                                    <button type="button" onClick={() => { setShowSupplierDropdown(null); setSupplierSearchQuery(''); }} className="text-xs text-gray-500 hover:text-gray-700">
                                      Close
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Product Catalog */}
            <div>
              <h4 className="text-gray-700 font-semibold mb-3">Product Catalog - Click to Add</h4>
              {filteredProducts.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {filteredProducts.map(product => (
                    <div
                      key={product.id}
                      onClick={() => handleToggleProduct(product)}
                      className="flex items-center gap-3 p-3 bg-gray-50 hover:bg-blue-50 border border-gray-200 hover:border-blue-300 rounded-xl cursor-pointer transition-all"
                    >
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                        {product.imageUrls?.[0] || product.imageUrl ? (
                          <img src={product.imageUrls?.[0] || product.imageUrl} alt={product.name} className="w-full h-full object-cover" />
                        ) : (
                          <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                          </svg>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h5 className="text-gray-900 font-medium text-sm truncate">{product.name}</h5>
                        <p className="text-gray-500 text-xs">{product.partNumber || product.sku || 'No Part #'}</p>
                        <span className="text-green-600 font-bold text-sm">{formatCurrency(product.price || product.sellingPrice || 0)}</span>
                      </div>
                      <svg className="w-6 h-6 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">No products found</div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-gray-600">{tempSelected.length} products selected</span>
            {tempSelected.length > 0 && (
              <span className="text-sm font-medium text-gray-900">
                Total: {formatCurrency(tempSelected.reduce((sum, p) => sum + (p.price * p.quantity), 0))}
              </span>
            )}
          </div>

          {tempSelected.length > 0 && tempSelected.some(p => !p.supplierId) && (
            <div className="mb-3 p-2 bg-yellow-100 border border-yellow-300 rounded-lg">
              <p className="text-yellow-800 text-xs">⚠️ {tempSelected.filter(p => !p.supplierId).length} product(s) need a supplier assigned</p>
            </div>
          )}

          <button
            onClick={handleConfirm}
            disabled={tempSelected.length === 0 || tempSelected.some(p => !p.supplierId)}
            className="w-full py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {tempSelected.length === 0 ? 'Select Products' : tempSelected.some(p => !p.supplierId) ? 'Assign Suppliers to Continue' : mode === 'edit' ? `Save Changes (${tempSelected.length})` : `Add ${tempSelected.length} Products`}
          </button>
        </div>
      </div>
    </div>
  );
};



const CreateInvoiceModal = ({ onClose, onSubmit, onNavigateToAddProduct, isModal = true }) => {
  const { vendorProfile } = useAuth();
  const { showAlert } = useNotification();

  const [step, setStep] = useState(1);

  // Generate invoice number once and store it
  const [invoiceNumber] = useState(() => {
    const prefix = 'INV';
    const date = new Date();
    const timestamp = date.getTime().toString().slice(-6);
    return `${prefix}-${date.getFullYear()}-${timestamp}`;
  });

  const [saleMode, setSaleMode] = useState('offline'); // 'offline' or 'online'
  const [suppliers, setSuppliers] = useState([]);
  const [porters, setPorters] = useState([]);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [activePickupIndex, setActivePickupIndex] = useState(0);

  // Add after existing state declarations
  const [filteredDeliveryCustomers, setFilteredDeliveryCustomers] = useState([]);
  const [showDeliveryCustomerDropdown, setShowDeliveryCustomerDropdown] = useState(false);

  // Rest of your existing state...
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    customerAddress: '',
    customerGST: '',
    items: [], // Remove the default manual item
    subtotal: 0,
    invoiceType: 'with_gst',
    totalTaxAmount: 0,
    discount: 0,
    totalAmount: 0,
    invoiceDate: new Date().toISOString().split('T')[0],
    notes: '',
    terms: 'Returns accepted within 7 days of delivery, subject to inspection and in original condition.',
    deliveryLandmark: '',
    deliveryLatitude: '',
    deliveryLongitude: '',
    pickups: [{
      address: '',
      landmark: '',
      contactName: '',
      contactPhone: '',
      latitude: '',
      longitude: '',
      products: []
    }],
    porterId: '',
    priority: 'normal',
    estimatedTime: '45 mins',
    paymentMode: 'cash',
    isFragile: false,
    specialInstructions: '',
  });

  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showProductModal, setShowProductModal] = useState(false);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);


  // Add these new state variables after existing ones
  const [selectedProducts, setSelectedProducts] = useState([]); // Products with supplier assignments
  const [showOnlineProductModal, setShowOnlineProductModal] = useState(false);
  const [productModalMode, setProductModalMode] = useState('add'); // 'add' or 'edit'





  // Group products by supplier for display (for online mode)
// Group products by supplier for display
const groupedPickups = React.useMemo(() => {
  const groups = {};
  selectedProducts.forEach(product => {
    if (!product.supplierId) return;
    if (!groups[product.supplierId]) {
      groups[product.supplierId] = {
        supplierId: product.supplierId,
        supplierName: product.supplierName,
        supplierPhone: product.supplierPhone,
        supplierEmail: product.supplierEmail || '',  // ADD THIS
        supplierAddress: product.supplierAddress,
        supplierLandmark: product.supplierLandmark || '',
        supplierLatitude: product.supplierLatitude || '',
        supplierLongitude: product.supplierLongitude || '',
        products: []
      };
    }
    groups[product.supplierId].products.push(product);
  });
  return Object.values(groups);
}, [selectedProducts]);

  // Calculate totals from selectedProducts
  const newOnlineTotalProducts = selectedProducts.length;
  const newOnlineTotalItems = selectedProducts.reduce((sum, p) => sum + p.quantity, 0);
  const newOnlineTotalAmount = selectedProducts.reduce((sum, p) => sum + (p.price * p.quantity), 0);


  // Handle products selected from new modal
  const handleNewProductsSelected = (products) => {
    setSelectedProducts(products);
  };

  // Handle supplier added
  const handleNewSupplierAdded = (supplier) => {
    // Supplier will be auto-added via the Firestore subscription
  };

  // Remove product from a pickup
  const removeProductFromGroupedPickup = (pickupIndex, productIndex) => {
    const pickup = groupedPickups[pickupIndex];
    const productToRemove = pickup.products[productIndex];

    setSelectedProducts(prev => prev.filter(p => {
      if (productToRemove.uniqueKey && p.uniqueKey) {
        return p.uniqueKey !== productToRemove.uniqueKey;
      }
      return !(p.id === productToRemove.id && p.supplierId === productToRemove.supplierId);
    }));
  };

  // Update product quantity
  const updateGroupedProductQuantity = (pickupIndex, productIndex, quantity) => {
    if (quantity < 1) {
      removeProductFromGroupedPickup(pickupIndex, productIndex);
      return;
    }
    const pickup = groupedPickups[pickupIndex];
    const productToUpdate = pickup.products[productIndex];

    setSelectedProducts(prev => prev.map(p => {
      if (productToUpdate.uniqueKey && p.uniqueKey) {
        return p.uniqueKey === productToUpdate.uniqueKey ? { ...p, quantity } : p;
      }
      if (p.id === productToUpdate.id && p.supplierId === productToUpdate.supplierId) {
        return { ...p, quantity };
      }
      return p;
    }));
  };



  // New Online mode submit handler using grouped pickups
// New Online mode submit handler using grouped pickups
// In CreateInvoiceModal.jsx - Update the handleOnlineSubmitNew function

const handleOnlineSubmitNew = async () => {
  setError('');
  setIsSubmitting(true);

  try {
    if (!formData.customerName || !formData.customerPhone) {
      setError('Please fill in customer details');
      setIsSubmitting(false);
      return;
    }

    if (!formData.customerAddress) {
      setError('Please fill in delivery address');
      setIsSubmitting(false);
      return;
    }

    if (groupedPickups.length === 0) {
      setError('Please add at least one product with a supplier');
      setIsSubmitting(false);
      return;
    }

    // FIX: Get the selected porter correctly
    const selectedPorter = formData.porterId ? porters.find(p => p.id === formData.porterId) : null;
    
    console.log('🚚 Porter Assignment Debug:', {
      porterId: formData.porterId,
      selectedPorter: selectedPorter,
      portersAvailable: porters.length
    });

    // 1. Create Invoice First
    const invoiceData = {
      invoiceNumber: invoiceNumber,
      customerName: formData.customerName.trim(),
      customerEmail: formData.customerEmail?.trim() || '',
      customerPhone: formData.customerPhone.trim(),
      customerAddress: formData.customerAddress.trim(),
      customerGST: '',
      saleMode: 'online',
      items: selectedProducts.map(p => ({
        productId: p.id,
        name: p.name,
        quantity: p.quantity,
        rate: p.price,
        amount: p.price * p.quantity,
        isManual: false,
        hsnCode: p.hsnCode || '',
        gstRate: p.gstRate || 0
      })),
      subtotal: newOnlineTotalAmount,
      discount: 0,
      invoiceType: 'without_gst',
      totalTaxAmount: 0,
      totalAmount: newOnlineTotalAmount,
      createdAt: Timestamp.fromDate(new Date()),
      notes: `Online delivery order - ${groupedPickups.length} pickup(s)`,
      terms: formData.terms || '',
      status: 'pending',
      paymentMethod: formData.paymentMode,
      vendorId: vendorProfile.uid,
    };

    const invoiceRef = await addDoc(collection(db, 'invoices'), invoiceData);
    console.log('✅ Invoice created:', invoiceRef.id);

    // 2. Create Task - FIX: Ensure porter fields are set correctly
    const taskData = {
      vendorId: vendorProfile.uid,
      createdByMainAdmin: true,
      saleMode: 'online',
      invoiceNumber: invoiceNumber,
      invoiceId: invoiceRef.id,
      orderId: null,
      
      // FIX: Set status based on porter assignment
      status: formData.porterId && formData.porterId !== '' ? 'pending' : 'unassigned',

      customer: {
        name: formData.customerName,
        phone: formData.customerPhone,
        email: formData.customerEmail || null,
      },

      pickups: groupedPickups.map(pickup => ({
        address: pickup.supplierAddress || '',
        landmark: pickup.supplierLandmark || null,
        contactName: pickup.supplierName,
        contactPhone: pickup.supplierPhone || null,
        contactEmail: pickup.supplierEmail || null,
        supplierId: pickup.supplierId,
        latitude: pickup.supplierLatitude ? parseFloat(pickup.supplierLatitude) : null,
        longitude: pickup.supplierLongitude ? parseFloat(pickup.supplierLongitude) : null,
        products: pickup.products.map(prod => ({
          id: prod.id,
          name: prod.name,
          partNumber: prod.partNumber,
          price: prod.price,
          quantity: prod.quantity,
          imageUrl: prod.imageUrl || null,
        })),
        productCount: pickup.products.length,
        itemCount: pickup.products.reduce((sum, prod) => sum + prod.quantity, 0),
        amount: pickup.products.reduce((sum, prod) => sum + (prod.price * prod.quantity), 0),
      })),

      delivery: {
        address: formData.customerAddress,
        landmark: formData.deliveryLandmark || null,
        latitude: formData.deliveryLatitude ? parseFloat(formData.deliveryLatitude) : null,
        longitude: formData.deliveryLongitude ? parseFloat(formData.deliveryLongitude) : null,
      },

      package: {
        products: selectedProducts.map(p => ({
          id: p.id,
          name: p.name,
          partNumber: p.partNumber,
          price: p.price,
          quantity: p.quantity,
          imageUrl: p.imageUrl || null,
        })),
        description: selectedProducts.map(p => p.name).join(', '),
        isFragile: formData.isFragile || false,
        specialInstructions: formData.specialInstructions || null,
        itemCount: newOnlineTotalItems,
        productCount: newOnlineTotalProducts,
      },

      payment: {
        mode: formData.paymentMode,
        amount: newOnlineTotalAmount,
        collected: formData.paymentMode === 'prepaid',
        deposited: false,
        status: formData.paymentMode === 'prepaid' ? 'verified' : 'pending',
      },

      // FIX: Porter assignment - ensure these are set correctly
      porterId: formData.porterId && formData.porterId !== '' ? formData.porterId : null,
      porterName: selectedPorter ? selectedPorter.name : null,
      
      priority: formData.priority || 'normal',
      estimatedTime: formData.estimatedTime || '45 mins',
      pickupCount: groupedPickups.length,

      timeline: [{
        status: 'created',
        timestamp: new Date().toISOString(),
        notes: formData.porterId && formData.porterId !== '' 
          ? `Task created and assigned to ${selectedPorter?.name || 'porter'}` 
          : 'Task created (unassigned)'
      }],

      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    console.log('📦 Task data being saved:', {
      porterId: taskData.porterId,
      porterName: taskData.porterName,
      status: taskData.status
    });

    const taskRef = await addDoc(collection(db, 'tasks'), taskData);
    console.log('✅ Task created:', taskRef.id);

    // 3. Update invoice with task reference
    await updateDoc(doc(db, 'invoices', invoiceRef.id), { taskId: taskRef.id });

    // 4. NOTIFY EACH SUPPLIER - Creates orders in their account
    const taskWithIds = {
      ...taskData,
      taskId: taskRef.id,
      invoiceId: invoiceRef.id
    };

    for (const pickup of groupedPickups) {
      const supplierEmail = pickup.supplierEmail || pickup.products[0]?.supplierEmail;
      if (supplierEmail) {
        try {
          await createSupplierOrderNotification(taskWithIds, {
            ...pickup,
            supplierEmail: supplierEmail
          }, vendorProfile);
          console.log(`✅ Notified supplier: ${pickup.supplierName}`);
        } catch (notifyError) {
          console.error(`Failed to notify supplier ${pickup.supplierName}:`, notifyError);
        }
      }
    }

    // 5. If porter was assigned, create a notification for the porter
    if (formData.porterId && formData.porterId !== '' && selectedPorter) {
      try {
        await addDoc(collection(db, 'notifications'), {
          recipientId: formData.porterId,
          recipientType: 'porter',
          type: 'task_assigned',
          title: 'New Task Assigned',
          message: `You have been assigned a new delivery task #${invoiceNumber}`,
          taskId: taskRef.id,
          invoiceId: invoiceRef.id,
          read: false,
          createdAt: Timestamp.fromDate(new Date()),
        });
        console.log('✅ Porter notification sent');
      } catch (notifyError) {
        console.error('Failed to notify porter:', notifyError);
      }
    }

    showAlert('Delivery task and invoice created successfully! Suppliers have been notified.', 'success');

    onSubmit?.({ type: 'task', invoiceNumber, invoiceId: invoiceRef.id, taskId: taskRef.id });
    if (onClose) onClose();
  } catch (error) {
    console.error('Error creating task:', error);
    setError(`Failed to create delivery task: ${error.message}`);
  } finally {
    setIsSubmitting(false);
  }
};



  useEffect(() => {
    loadProducts();
    loadCustomers();
  }, []);

  const loadProducts = async () => {
    try {
      if (vendorProfile?.uid) {
        const result = await getProducts(vendorProfile.uid, { pageSize: 100 });
        setProducts(result.products || []);
      }
    } catch (error) {
      console.error('Error loading products:', error);
      setError('Failed to load products');
    }
  };

  const loadCustomers = async () => {
    try {
      if (vendorProfile?.uid) {
        const customersQuery = query(
          collection(db, 'customers'),
          where('vendorId', '==', vendorProfile.uid)
        );
        const customersSnapshot = await getDocs(customersQuery);
        const customersList = customersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setCustomers(customersList);
      }
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const handleCustomerSearch = (value) => {
    setFormData(prev => ({ ...prev, customerName: value }));

    if (value.trim()) {
      const filtered = customers.filter(customer =>
        customer.name.toLowerCase().includes(value.toLowerCase()) ||
        (customer.phone && customer.phone.includes(value)) ||
        (customer.email && customer.email.toLowerCase().includes(value.toLowerCase()))
      );
      setFilteredCustomers(filtered);
      setShowCustomerDropdown(true);
    } else {
      setShowCustomerDropdown(false);
      // Clear other customer fields when name is cleared
      setFormData(prev => ({
        ...prev,
        customerEmail: '',
        customerPhone: '',
        customerAddress: '',
        customerGST: ''
      }));
    }
  };

  const handleCustomerSelect = (customer) => {
    setFormData(prev => ({
      ...prev,
      customerName: customer.name,
      customerEmail: customer.email || '',
      customerPhone: customer.phone || '',
      customerAddress: customer.address || '',
      customerGST: customer.gstNumber || ''
    }));
    setShowCustomerDropdown(false);
  };

  const handleAddCustomer = () => {
    setShowCustomerModal(true);
  };

  const handleCustomerSaved = (savedCustomer) => {
    // Reload customers list
    loadCustomers();
    // Optionally select the newly added customer
    if (savedCustomer) {
      handleCustomerSelect(savedCustomer);
    }
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + (item.amount || 0), 0);
    const discountPercentage = parseFloat(formData.discount) || 0;
    const discountAmount = (subtotal * discountPercentage) / 100;
    const taxableAmount = subtotal - discountAmount;

    let totalTaxAmount = 0;
    let totalAmount = taxableAmount;

    if (formData.invoiceType === 'with_gst') {
      // Calculate total tax from individual items
      totalTaxAmount = formData.items.reduce((sum, item) => {
        const itemTaxableAmount = (item.amount || 0) - ((item.amount || 0) * discountPercentage) / 100;
        const itemTaxAmount = (itemTaxableAmount * (item.gstRate || 0)) / 100;
        return sum + itemTaxAmount;
      }, 0);
      totalAmount = taxableAmount + totalTaxAmount;
    }

    setFormData(prev => ({
      ...prev,
      subtotal,
      totalTaxAmount,
      totalAmount
    }));
  };

  useEffect(() => {
    calculateTotals();
  }, [formData.items, formData.discount, formData.invoiceType]);


  // Load suppliers
  useEffect(() => {
    if (!vendorProfile?.uid) return;

    const customersQuery = query(
      collection(db, 'customers'),
      where('vendorId', '==', vendorProfile.uid)
      // REMOVED: where('type', '==', 'supplier')
    );

    const unsubscribe = onSnapshot(customersQuery, (snapshot) => {
      const suppliersList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setSuppliers(suppliersList);
    });

    return () => unsubscribe();
  }, [vendorProfile?.uid]);

  // Load porters (for online mode)
  useEffect(() => {
    const portersQuery = query(collection(db, 'porters'), where('isActive', '==', true));
    const unsubscribe = onSnapshot(portersQuery, (snapshot) => {
      const portersList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setPorters(portersList);
    });
    return () => unsubscribe();
  }, []);

  // Auto-fill first pickup from vendor profile (for online mode)
  useEffect(() => {
    if (vendorProfile?.address && saleMode === 'online') {
      const addressStr = typeof vendorProfile.address === 'string'
        ? vendorProfile.address
        : `${vendorProfile.address.street || ''}, ${vendorProfile.address.area || ''}, ${vendorProfile.address.city || ''} - ${vendorProfile.address.pincode || ''}`;

      setFormData(prev => ({
        ...prev,
        pickups: [{
          ...prev.pickups[0],
          address: addressStr,
          contactName: vendorProfile.contactPersonName || vendorProfile.ownerName || vendorProfile.shopName || '',
          contactPhone: vendorProfile.phone || '',
          latitude: vendorProfile.location?.latitude?.toString() || '',
          longitude: vendorProfile.location?.longitude?.toString() || '',
        }]
      }));
    }
  }, [vendorProfile, saleMode]);


  const handleInputChange = (field, value) => {
    if (field === 'discount') {
      const newValue = Math.max(0, Math.min(100, parseFloat(value) || 0));
      setFormData(prev => ({ ...prev, [field]: newValue }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    if (field === 'quantity') {
      newItems[index][field] = parseInt(value) || 0;
    } else if (field === 'rate') {
      newItems[index][field] = parseFloat(value) || 0;
    } else if (field === 'gstRate') {
      newItems[index][field] = parseFloat(value) || 0;
    } else {
      newItems[index][field] = value;
    }

    // Calculate item amount
    newItems[index].amount = (newItems[index].quantity || 0) * (newItems[index].rate || 0);

    setFormData(prev => ({ ...prev, items: newItems }));
  };

  const handleProductModalOpen = () => {
    setShowProductModal(true);
  };

  const handleProductSelect = (selectedProducts) => {
    const newProductItems = selectedProducts.map(product => ({
      productId: product.productId,
      name: product.name,
      quantity: product.quantity || 1,
      rate: product.rate || 0,
      amount: (product.quantity || 1) * (product.rate || 0),
      isManual: false,
      hsnCode: product.hsnCode || '',
      gstRate: product.gstRate || 18
    }));

    // Keep existing manual items and add new product items
    setFormData(prev => ({
      ...prev,
      items: [
        ...prev.items.filter(item => item.isManual), // Keep manual items
        ...newProductItems // Add new product items
      ]
    }));
    setShowProductModal(false);
  };


  // Add this function after handleCustomerSearch
  const handleDeliveryCustomerSearch = (value) => {
    setFormData(prev => ({ ...prev, customerName: value }));

    if (value.trim()) {
      const filtered = customers.filter(customer =>
        customer.name?.toLowerCase().includes(value.toLowerCase()) ||
        (customer.phone && customer.phone.includes(value))
      );
      setFilteredDeliveryCustomers(filtered);
      setShowDeliveryCustomerDropdown(true);
    } else {
      setShowDeliveryCustomerDropdown(false);
      // Clear other fields when name is cleared
      setFormData(prev => ({
        ...prev,
        customerEmail: '',
        customerPhone: '',
        customerAddress: '',
        deliveryLandmark: '',
        deliveryLatitude: '',
        deliveryLongitude: ''
      }));
    }
  };

  const handleDeliveryCustomerSelect = (customer) => {
    setFormData(prev => ({
      ...prev,
      customerName: customer.name,
      customerEmail: customer.email || '',
      customerPhone: customer.phone || '',
      customerAddress: customer.address || '',
    }));
    setShowDeliveryCustomerDropdown(false);
  };


  const addManualItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { productId: '', name: '', quantity: 1, rate: 0, amount: 0, isManual: true, hsnCode: '', gstRate: 18 }]
    }));
  };

  const addProductItem = () => {
    setShowProductModal(true);
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, items: newItems }));
  };

  const handleNavigateToAddProduct = () => {
    console.log('Add new product clicked'); // Debug log
    console.log('onNavigateToAddProduct prop:', typeof onNavigateToAddProduct); // Debug log

    if (onNavigateToAddProduct && typeof onNavigateToAddProduct === 'function') {
      console.log('Calling navigation function'); // Debug log
      onNavigateToAddProduct();
    } else {
      console.log('Navigation function not available'); // Debug log
      showAlert('Navigation function not provided', 'warning');
    }
  };



  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Helper function to calculate CGST and SGST
  const calculateCGSTSGST = (taxableAmount, gstRate) => {
    const totalGSTAmount = (taxableAmount * gstRate) / 100;
    const cgstAmount = totalGSTAmount / 2;
    const sgstAmount = totalGSTAmount / 2;
    return { cgstAmount, sgstAmount, totalGSTAmount };
  };

  // Calculate total amount including GST for each item
  const calculateItemTotalWithGST = (item, discountPercentage = 0) => {
    const itemDiscountAmount = (item.amount * discountPercentage) / 100;
    const itemTaxableAmount = item.amount - itemDiscountAmount;
    if (formData.invoiceType === 'with_gst') {
      const { totalGSTAmount } = calculateCGSTSGST(itemTaxableAmount, item.gstRate || 0);
      return itemTaxableAmount + totalGSTAmount;
    }
    return itemTaxableAmount;
  };

  // NEW: Update product stock when selling (decrease stock)
  const updateProductStockForSale = async (items) => {
    const stockUpdates = [];

    try {
      for (const item of items) {
        if (item.productId) {
          const productRef = doc(db, 'products', item.productId);
          const productSnap = await getDoc(productRef);

          if (productSnap.exists()) {
            const currentStock = productSnap.data().stock || 0;
            const quantitySold = parseInt(item.quantity);
            const newStock = Math.max(0, currentStock - quantitySold); // Prevent negative stock

            await updateDoc(productRef, {
              stock: newStock,
              updatedAt: Timestamp.fromDate(new Date())
            });

            stockUpdates.push({
              productId: item.productId,
              productName: item.name,
              previousStock: currentStock,
              newStock: newStock,
              quantitySold: quantitySold,
              saleAmount: item.amount
            });

            // Log stock decrease
            console.log(`Stock updated for ${item.name}: ${currentStock} -> ${newStock} (sold: ${quantitySold})`);
          }
        }
      }

      return stockUpdates;
    } catch (error) {
      console.error('Error updating product stock for sale:', error);
      throw error;
    }
  };




  // ==================== ONLINE MODE HANDLERS ====================

  // Calculate online totals
  const onlineTotalProducts = formData.pickups.reduce((sum, p) => sum + p.products.length, 0);
  const onlineTotalItems = formData.pickups.reduce((sum, p) => sum + p.products.reduce((s, prod) => s + (prod.quantity || 1), 0), 0);
  const onlineTotalAmount = formData.pickups.reduce((sum, p) => sum + p.products.reduce((s, prod) => s + ((prod.rate || prod.price || 0) * (prod.quantity || 1)), 0), 0);

  const addPickupLocation = () => {
    setFormData(prev => ({
      ...prev,
      pickups: [...prev.pickups, {
        address: '',
        landmark: '',
        contactName: '',
        contactPhone: '',
        latitude: '',
        longitude: '',
        products: []
      }]
    }));
  };

  const updatePickup = (index, updatedPickup) => {
    setFormData(prev => ({
      ...prev,
      pickups: prev.pickups.map((p, i) => i === index ? updatedPickup : p)
    }));
  };

  const removePickup = (index) => {
    setFormData(prev => ({
      ...prev,
      pickups: prev.pickups.filter((_, i) => i !== index)
    }));
  };

  const handleAddProductsToPickup = (pickupIndex) => {
    setActivePickupIndex(pickupIndex);
    setShowProductModal(true);
  };

  // Replace existing handleOnlineProductsSelected function
  const handleOnlineProductsSelected = (selectedProducts) => {
    setFormData(prev => {
      const newPickups = [...prev.pickups];
      newPickups[activePickupIndex] = {
        ...newPickups[activePickupIndex],
        products: selectedProducts.map(p => ({
          id: p.productId || p.id,
          productId: p.productId || p.id,
          name: p.name,
          quantity: p.quantity || 1,
          rate: p.rate || p.price || 0,
          price: p.rate || p.price || 0,
          imageUrl: p.imageUrl || (products.find(prod => prod.id === (p.productId || p.id))?.imageUrls?.[0]) || null,
          partNumber: p.partNumber || p.hsnCode || '',
          hsnCode: p.hsnCode || '',
          gstRate: p.gstRate || 18,
          stock: p.stock || 0
        }))
      };
      return { ...prev, pickups: newPickups };
    });
  };
  const removeProductFromPickup = (pickupIndex, productIndex) => {
    setFormData(prev => {
      const newPickups = [...prev.pickups];
      newPickups[pickupIndex] = {
        ...newPickups[pickupIndex],
        products: newPickups[pickupIndex].products.filter((_, i) => i !== productIndex)
      };
      return { ...prev, pickups: newPickups };
    });
  };

  const updatePickupProductQuantity = (pickupIndex, productIndex, quantity) => {
    if (quantity < 1) return;
    setFormData(prev => {
      const newPickups = [...prev.pickups];
      newPickups[pickupIndex] = {
        ...newPickups[pickupIndex],
        products: newPickups[pickupIndex].products.map((p, i) =>
          i === productIndex ? { ...p, quantity } : p
        )
      };
      return { ...prev, pickups: newPickups };
    });
  };

  const handleSupplierSaved = () => {
    // Suppliers list will auto-refresh via subscription
  };

  // Online mode submit handler
  const handleOnlineSubmit = async () => {
    setError('');
    setIsSubmitting(true);

    try {
      if (!formData.customerName || !formData.customerPhone) {
        setError('Please fill in customer details');
        setIsSubmitting(false);
        return;
      }

      if (!formData.customerAddress) {
        setError('Please fill in delivery address');
        setIsSubmitting(false);
        return;
      }

      const validPickups = formData.pickups.filter(p => p.address.trim() && p.products.length > 0);
      if (validPickups.length === 0) {
        setError('Please add at least one pickup location with products');
        setIsSubmitting(false);
        return;
      }

      const selectedPorter = porters.find(p => p.id === formData.porterId);
      const allProducts = formData.pickups.flatMap(p => p.products);

      const taskData = {
        vendorId: vendorProfile.uid,
        createdByMainAdmin: true,
        saleMode: 'online',

        invoiceNumber: invoiceNumber,
        orderId: null,
        status: formData.porterId ? 'pending' : 'unassigned',

        customer: {
          name: formData.customerName,
          phone: formData.customerPhone,
          email: formData.customerEmail || null,
        },

        pickups: formData.pickups.filter(p => p.address.trim()).map(p => ({
          address: p.address,
          landmark: p.landmark || null,
          contactName: p.contactName || null,
          contactPhone: p.contactPhone || null,
          latitude: p.latitude ? parseFloat(p.latitude) : null,
          longitude: p.longitude ? parseFloat(p.longitude) : null,
          products: p.products.map(prod => ({
            id: prod.productId,
            name: prod.name,
            partNumber: prod.partNumber,
            price: prod.rate || prod.price,
            quantity: prod.quantity,
            imageUrl: prod.imageUrl || null,
          })),
          productCount: p.products.length,
          itemCount: p.products.reduce((sum, prod) => sum + (prod.quantity || 1), 0),
          amount: p.products.reduce((sum, prod) => sum + ((prod.rate || prod.price || 0) * (prod.quantity || 1)), 0),
        })),

        delivery: {
          address: formData.customerAddress,
          landmark: formData.deliveryLandmark || null,
          latitude: formData.deliveryLatitude ? parseFloat(formData.deliveryLatitude) : null,
          longitude: formData.deliveryLongitude ? parseFloat(formData.deliveryLongitude) : null,
        },

        package: {
          products: allProducts.map(p => ({
            id: p.productId,
            name: p.name,
            partNumber: p.partNumber,
            price: p.rate || p.price,
            quantity: p.quantity,
            imageUrl: p.imageUrl || null,
          })),
          description: allProducts.map(p => p.name).join(', '),
          isFragile: formData.isFragile,
          specialInstructions: formData.specialInstructions || null,
          itemCount: onlineTotalItems,
          productCount: onlineTotalProducts,
        },

        payment: {
          mode: formData.paymentMode,
          amount: onlineTotalAmount,
          collected: formData.paymentMode === 'prepaid',
          deposited: false,
          status: formData.paymentMode === 'prepaid' ? 'verified' : 'pending',
        },

        porterId: formData.porterId || null,
        porterName: selectedPorter?.name || null,
        priority: formData.priority,
        estimatedTime: formData.estimatedTime,
        pickupCount: validPickups.length,

        timeline: [{
          status: 'created',
          timestamp: new Date().toISOString(),
          notes: 'Task created'
        }],

        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      await addDoc(collection(db, 'tasks'), taskData);
      showAlert('Delivery task created successfully!', 'success');

      onSubmit?.({ type: 'task', invoiceNumber });
      if (onClose) onClose();
    } catch (error) {
      console.error('Error creating task:', error);
      setError(`Failed to create delivery task: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };



  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate form before preview
    if (!formData.customerName.trim()) {
      setError('Customer name is required');
      return;
    }

    // Validate GST number for GST invoices
    if (formData.invoiceType === 'with_gst' && !formData.customerGST.trim()) {
      setError('Customer GST number is required for GST invoices');
      return;
    }

    const validItems = formData.items.filter(item =>
      item.name.trim() && item.quantity > 0 && item.rate > 0
    );

    if (validItems.length === 0) {
      setError('At least one item is required. Please add products or manual items.');
      return;
    }

    // Check stock availability for products
    try {
      const stockValidation = await validateBulkStockAvailability(validItems);

      if (!stockValidation.canProceed) {
        const issues = stockValidation.itemResults
          .filter(result => !result.canSell)
          .map(result => `${result.productName}: Available ${result.currentStock}, Required ${result.requestedQuantity}`)
          .join('\n');

        setError(`Insufficient stock:\n${issues}`);
        return;
      }
    } catch (error) {
      console.error('Error validating stock:', error);
      setError('Failed to validate stock availability');
      return;
    }

    // Move to preview step
    setStep(2);
  };

  // Update the handleSubmit function to use the new functions
  const handleSubmit = async () => {
    setError('');
    setIsSubmitting(true);

    try {
      const validItems = formData.items.filter(item =>
        item.name.trim() && item.quantity > 0 && item.rate > 0
      );

      const invoiceData = {
        invoiceNumber: invoiceNumber,
        customerName: formData.customerName.trim(),
        customerEmail: formData.customerEmail.trim(),
        customerPhone: formData.customerPhone.trim(),
        customerAddress: formData.customerAddress.trim(),
        customerGST: formData.customerGST.trim(),
        items: validItems,
        subtotal: formData.subtotal,
        discount: parseFloat(formData.discount) || 0,
        invoiceType: formData.invoiceType,
        totalTaxAmount: formData.totalTaxAmount,
        totalAmount: formData.totalAmount,
        createdAt: Timestamp.fromDate(new Date(formData.invoiceDate)),
        notes: formData.notes.trim(),
        terms: formData.terms.trim(),
        status: 'paid',
        paidAt: Timestamp.fromDate(new Date()),
        paymentMethod: 'cash'
      };

      const result = await createInvoice(invoiceData);

      // Update product stock for sold items
      try {
        const stockUpdates = await updateProductStockForSale(validItems);

        // Update sales tracking
        await updateProductSalesTracking(validItems);

        let stockUpdateMessage = `Sales invoice created successfully! Stock updated for ${stockUpdates.length} products:`;
        stockUpdates.forEach(update => {
          stockUpdateMessage += `\n• ${update.productName}: ${update.previousStock} → ${update.newStock} units (sold: ${update.quantitySold})`;
        });

        showAlert(stockUpdateMessage, 'success');

      } catch (stockError) {
        console.warn('Sales invoice created but stock update failed:', stockError);
        showAlert('Sales invoice created successfully, but stock update failed. Please update stock manually.', 'warning');
      }

      // Auto-print the invoice after creation
      setTimeout(() => {
        const success = printInvoice({ ...invoiceData, id: result.id }, vendorProfile);
        if (!success) {
          console.log('Print popup blocked - user can print later from invoice details');
        }
      }, 500);

      onSubmit?.(result);
      if (onClose) onClose();

    } catch (error) {
      console.error('Error creating invoice:', error);
      setError(`Failed to create invoice: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const getSelectedProductIds = () => {
    return formData.items.map(item => item.productId).filter(id => id);
  };

  const getGstBreakdown = () => {
    const gstBreakdown = {};
    formData.items.forEach(item => {
      const rate = item.gstRate || 0;
      const discountPercentage = parseFloat(formData.discount) || 0;
      const itemTaxableAmount = (item.amount || 0) - ((item.amount || 0) * discountPercentage) / 100;
      const { cgstAmount, sgstAmount, totalGSTAmount } = calculateCGSTSGST(itemTaxableAmount, rate);

      if (rate > 0) {
        if (!gstBreakdown[rate]) {
          gstBreakdown[rate] = { taxableAmount: 0, cgstAmount: 0, sgstAmount: 0, totalGSTAmount: 0 };
        }
        gstBreakdown[rate].taxableAmount += itemTaxableAmount;
        gstBreakdown[rate].cgstAmount += cgstAmount;
        gstBreakdown[rate].sgstAmount += sgstAmount;
        gstBreakdown[rate].totalGSTAmount += totalGSTAmount;
      }
    });
    return gstBreakdown;
  };

  // Preview Step
  if (step === 2) {
    const discountAmount = (formData.subtotal * (parseFloat(formData.discount) || 0)) / 100;
    const gstBreakdown = getGstBreakdown();

    const content = (
      <div className="bg-white">
        {/* Print Styles */}
        <style jsx>{`
  @media print {
    /* Force exact colors and better rendering */
    * {
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
      box-sizing: border-box !important;
    }
    
    /* Hide everything first, then show only print content */
    body * {
      visibility: hidden;
    }
    
    .print-content, .print-content * {
      visibility: visible;
    }
    
    /* Print content positioning and sizing */
    .print-content {
      position: absolute !important;
      left: 0 !important;
      top: 0 !important;
      width: 100% !important;
      margin: 0 !important;
      padding: 15px !important;
      font-size: 12px !important;
      line-height: 1.3 !important;
      font-family: Arial, sans-serif !important;
      background: white !important;
    }
    
    /* Hide non-print elements */
    .no-print {
      display: none !important;
    }
    
    /* Page setup for A4 */
    @page {
      size: A4;
      margin: 0.4in;
    }
    
    /* Heading sizes - scaled down proportionally */
    .print-content h1 {
      font-size: 24px !important;
      margin: 0 0 10px 0 !important;
      font-weight: bold !important;
    }
    
    .print-content h2 {
      font-size: 18px !important;
      margin: 0 0 8px 0 !important;
      font-weight: bold !important;
    }
    
    .print-content h3 {
      font-size: 16px !important;
      margin: 0 0 6px 0 !important;
      font-weight: bold !important;
    }
    
    .print-content h4 {
      font-size: 14px !important;
      margin: 0 0 4px 0 !important;
      font-weight: bold !important;
    }
    
    .print-content h5 {
      font-size: 12px !important;
      margin: 0 0 3px 0 !important;
      font-weight: bold !important;
    }
    
    /* Paragraph and text sizing */
    .print-content p {
      margin: 0 0 3px 0 !important;
      font-size: 11px !important;
    }
    
    .print-content div {
      margin: 0 !important;
    }
    
    /* Table optimizations - keep structure, reduce size */
    .print-content table {
      width: 100% !important;
      border-collapse: collapse !important;
      margin: 8px 0 !important;
      font-size: 10px !important;
      page-break-inside: auto !important;
    }
    
    .print-content thead {
      display: table-header-group !important;
    }
    
    .print-content tbody {
      display: table-row-group !important;
    }
    
    .print-content th {
      background-color: #f8f9fa !important;
      border: 1px solid #dee2e6 !important;
      padding: 4px 3px !important;
      font-size: 9px !important;
      font-weight: bold !important;
      text-align: center !important;
      vertical-align: middle !important;
      line-height: 1.1 !important;
    }
    
    .print-content td {
      border: 1px solid #dee2e6 !important;
      padding: 3px 2px !important;
      font-size: 10px !important;
      vertical-align: top !important;
      line-height: 1.2 !important;
    }
    
    /* Reduce spacing in existing layout classes */
    .print-content .mb-8 {
      margin-bottom: 8px !important;
    }
    
    .print-content .mb-6 {
      margin-bottom: 6px !important;
    }
    
    .print-content .mb-4 {
      margin-bottom: 4px !important;
    }
    
    .print-content .mb-3 {
      margin-bottom: 3px !important;
    }
    
    .print-content .mb-2 {
      margin-bottom: 2px !important;
    }
    
    .print-content .mt-8 {
      margin-top: 8px !important;
    }
    
    .print-content .mt-6 {
      margin-top: 6px !important;
    }
    
    .print-content .mt-4 {
      margin-top: 4px !important;
    }
    
    .print-content .mt-3 {
      margin-top: 3px !important;
    }
    
    .print-content .mt-2 {
      margin-top: 2px !important;
    }
    
    /* Padding reductions */
    .print-content .py-4 {
      padding-top: 2px !important;
      padding-bottom: 2px !important;
    }
    
    .print-content .py-3 {
      padding-top: 2px !important;
      padding-bottom: 2px !important;
    }
    
    .print-content .py-2 {
      padding-top: 1px !important;
      padding-bottom: 1px !important;
    }
    
    .print-content .px-4 {
      padding-left: 3px !important;
      padding-right: 3px !important;
    }
    
    .print-content .px-3 {
      padding-left: 2px !important;
      padding-right: 2px !important;
    }
    
    .print-content .px-2 {
      padding-left: 2px !important;
      padding-right: 2px !important;
    }
    
    /* Compact existing sections without changing structure */
    .print-content .p-8 {
      padding: 12px !important;
    }
    
    .print-content .p-6 {
      padding: 8px !important;
    }
    
    .print-content .p-4 {
      padding: 6px !important;
    }
    
    /* Text size adjustments */
    .print-content .text-3xl {
      font-size: 24px !important;
    }
    
    .print-content .text-2xl {
      font-size: 20px !important;
    }
    
    .print-content .text-xl {
      font-size: 18px !important;
    }
    
    .print-content .text-lg {
      font-size: 16px !important;
    }
    
    .print-content .text-base {
      font-size: 12px !important;
    }
    
    .print-content .text-sm {
      font-size: 11px !important;
    }
    
    .print-content .text-xs {
      font-size: 9px !important;
    }
    
    /* Ensure colors print properly */
    .print-content .text-gray-900 {
      color: #000 !important;
    }
    
    .print-content .text-gray-800 {
      color: #1f2937 !important;
    }
    
    .print-content .text-gray-700 {
      color: #374151 !important;
    }
    
    .print-content .text-gray-600 {
      color: #4b5563 !important;
    }
    
    .print-content .text-gray-500 {
      color: #6b7280 !important;
    }
    
    .print-content .text-purple-900 {
      color: #581c87 !important;
    }
    
    .print-content .text-red-600 {
      color: #dc2626 !important;
    }
    
    .print-content .text-orange-600 {
      color: #ea580c !important;
    }
    
    .print-content .text-green-600 {
      color: #16a34a !important;
    }
    
    .print-content .text-blue-600 {
      color: #2563eb !important;
    }
    
    /* Background colors for sections */
    .print-content .bg-gray-50 {
      background-color: #f9fafb !important;
    }
    
    .print-content .bg-blue-50 {
      background-color: #eff6ff !important;
    }
    
    .print-content .bg-purple-50 {
      background-color: #faf5ff !important;
    }
    
    .print-content .bg-yellow-50 {
      background-color: #fefce8 !important;
    }
    
    /* Borders */
    .print-content .border {
      border: 1px solid #d1d5db !important;
    }
    
    .print-content .border-gray-200 {
      border-color: #e5e7eb !important;
    }
    
    .print-content .border-gray-300 {
      border-color: #d1d5db !important;
    }
    
    .print-content .border-t {
      border-top: 1px solid #d1d5db !important;
    }
    
    .print-content .border-b {
      border-bottom: 1px solid #d1d5db !important;
    }
    
    .print-content .border-l {
      border-left: 1px solid #d1d5db !important;
    }
    
    .print-content .border-r {
      border-right: 1px solid #d1d5db !important;
    }
    
    /* Flex layouts - keep structure */
    .print-content .flex {
      display: flex !important;
    }
    
    .print-content .justify-between {
      justify-content: space-between !important;
    }
    
    .print-content .items-center {
      align-items: center !important;
    }
    
    .print-content .items-start {
      align-items: flex-start !important;
    }
    
    .print-content .space-x-6 > * + * {
      margin-left: 10px !important;
    }
    
    .print-content .space-x-3 > * + * {
      margin-left: 6px !important;
    }
    
    .print-content .space-x-2 > * + * {
      margin-left: 4px !important;
    }
    
    .print-content .space-y-2 > * + * {
      margin-top: 2px !important;
    }
    
    .print-content .space-y-1 > * + * {
      margin-top: 1px !important;
    }
    
    /* QR code sizing */
    .print-content img {
      max-width: 100% !important;
      height: auto !important;
    }
    
    /* Specific QR code adjustments */
    .print-content .w-48 {
      width: 80px !important;
    }
    
    .print-content .h-40 {
      height: 60px !important;
    }
    
    /* Grid layouts */
    .print-content .grid {
      display: grid !important;
    }
    
    .print-content .grid-cols-1 {
      grid-template-columns: repeat(1, minmax(0, 1fr)) !important;
    }
    
    .print-content .grid-cols-2 {
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    }
    
    .print-content .gap-6 {
      gap: 8px !important;
    }
    
    .print-content .gap-4 {
      gap: 6px !important;
    }
    
    /* Badges and small elements */
    .print-content .rounded-full {
      border-radius: 9999px !important;
    }
    
    .print-content .rounded-md {
      border-radius: 6px !important;
    }
    
    .print-content .rounded {
      border-radius: 4px !important;
    }
    
    /* Font weights */
    .print-content .font-bold {
      font-weight: 700 !important;
    }
    
    .print-content .font-semibold {
      font-weight: 600 !important;
    }
    
    .print-content .font-medium {
      font-weight: 500 !important;
    }
    
    /* Ensure table responsiveness is disabled in print */
    .print-content .overflow-x-auto {
      overflow-x: visible !important;
    }
    
    /* Make sure flex items don't shrink too much */
    .print-content .flex-1 {
      flex: 1 1 0% !important;
    }
    
    .print-content .flex-shrink-0 {
      flex-shrink: 0 !important;
    }
    
    /* Text alignment */
    .print-content .text-left {
      text-align: left !important;
    }
    
    .print-content .text-center {
      text-align: center !important;
    }
    
    .print-content .text-right {
      text-align: right !important;
    }
    
    /* Width constraints */
    .print-content .w-full {
      width: 100% !important;
    }
    
    .print-content .w-96 {
      width: 300px !important;
    }
    
    /* Handle very long content */
    .print-content .whitespace-pre-wrap {
      white-space: pre-wrap !important;
      word-wrap: break-word !important;
    }
    
    /* Ensure content doesn't break poorly */
    .print-content .page-break-inside-avoid {
      page-break-inside: avoid !important;
    }
    
    /* Make sure all text is readable */
    .print-content {
      color: #000 !important;
    }
    
    /* Specific adjustments for very small elements */
    .print-content .min-w-full {
      min-width: 100% !important;
    }
    
    .print-content .divide-y > * {
      border-top-width: 1px !important;
      border-color: #e5e7eb !important;
    }
    
    .print-content .divide-gray-200 > * {
      border-color: #e5e7eb !important;
    }
  }
`}</style>

        {/* Header Actions */}
        <div className="flex justify-between items-center mb-6 no-print">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Sales Invoice Preview</h3>

          </div>
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={() => setStep(1)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Edit
            </button>
            <button
              type="button"
              onClick={handlePrint}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Print
            </button>
            {isModal && (
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-300 rounded-md no-print">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <div className="print-content bg-white border border-gray-200 rounded-lg p-8">
          {/* Invoice Header */}
          <div className="flex justify-between items-start mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">INVOICE</h1>
              <div className="text-sm text-gray-600">
                <p><strong>Invoice #:</strong> {invoiceNumber}</p>
                <p><strong>Date:</strong> {formatDate(formData.invoiceDate)}</p>
                <p><strong>Status:</strong> <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">PAID</span></p>
              </div>
            </div>

            {formData.invoiceType === 'with_gst' && (
              <div className="text-right">
                <h2 className="text-xl font-bold text-gray-900 mb-2">
                  {vendorProfile?.shopName || 'Your Shop Name'}
                </h2>
                <div className="text-sm text-gray-600">
                  <p>{vendorProfile?.address || 'Shop Address'}</p>
                  <p>{vendorProfile?.city || 'City'}, {vendorProfile?.state || 'State'} {vendorProfile?.pincode || 'PIN'}</p>
                  <p>Phone: {vendorProfile?.phone || 'N/A'}</p>
                  <p>Email: {vendorProfile?.email || 'N/A'}</p>
                  {vendorProfile?.gstNumber && (
                    <p>GST: {vendorProfile.gstNumber}</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Bill To Section */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Bill To:</h3>
            <div className="bg-gray-50 p-4 rounded-md">
              <div className="text-sm">
                <p className="font-medium text-gray-900">{formData.customerName}</p>
                {formData.customerEmail && <p className="text-gray-600">{formData.customerEmail}</p>}
                {formData.customerPhone && <p className="text-gray-600">{formData.customerPhone}</p>}
                {formData.invoiceType === 'with_gst' && formData.customerGST && (
                  <p className="text-gray-600">GST: {formData.customerGST}</p>
                )}
                {formData.customerAddress && <p className="text-gray-600 mt-2">{formData.customerAddress}</p>}
              </div>
            </div>
          </div>

          {/* Items Table */}
          <div className="mb-8">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Invoice Items</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 border border-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                      Description
                    </th>
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                      HSN
                    </th>
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                      Qty
                    </th>
                    <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                      Rate (₹)
                    </th>
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                      GST (%)
                    </th>
                    <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                      CGST (₹)
                    </th>
                    <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                      SGST (₹)
                    </th>
                    <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                      Amount (₹)
                    </th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total (₹)
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {formData.items.filter(item => item.name.trim() && item.quantity > 0 && item.rate > 0).map((item, index) => {
                    const discountPercentage = parseFloat(formData.discount) || 0;
                    const itemDiscountAmount = (item.amount * discountPercentage) / 100;
                    const itemTaxableAmount = item.amount - itemDiscountAmount;
                    const { cgstAmount, sgstAmount } = calculateCGSTSGST(itemTaxableAmount, item.gstRate || 0);
                    const totalWithGST = calculateItemTotalWithGST(item, discountPercentage);

                    return (
                      <tr key={index}>
                        <td className="px-3 py-4 border-r">
                          <div className="text-sm font-medium text-gray-900">{item.name}</div>
                          {item.productId && (
                            <div className="text-xs text-gray-500">ID: {item.productId}</div>
                          )}
                        </td>
                        <td className="px-2 py-4 text-center border-r">
                          <span className="text-xs text-gray-900">{item.hsnCode || 'N/A'}</span>
                        </td>
                        <td className="px-2 py-4 text-center border-r">
                          <span className="text-sm text-gray-900">{item.quantity}</span>
                        </td>
                        <td className="px-2 py-4 text-right border-r">
                          <span className="text-sm text-gray-900">{formatCurrency(item.rate)}</span>
                        </td>
                        <td className="px-2 py-4 text-center border-r">
                          <span className="text-sm text-gray-900">{item.gstRate || 0}%</span>
                        </td>
                        <td className="px-2 py-4 text-right border-r">
                          <span className="text-xs text-gray-900">{formatCurrency(cgstAmount)}</span>
                        </td>
                        <td className="px-2 py-4 text-right border-r">
                          <span className="text-xs text-gray-900">{formatCurrency(sgstAmount)}</span>
                        </td>
                        <td className="px-2 py-4 text-right border-r">
                          <span className="text-sm font-medium text-gray-900">{formatCurrency(item.amount)}</span>
                        </td>
                        <td className="px-3 py-4 text-right">
                          <span className="text-sm font-bold text-gray-900">{formatCurrency(totalWithGST)}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals Section */}
          <div className="flex justify-end mb-8">
            <div className="w-96">
              <div className="bg-gray-50 p-6 rounded-md border">
                <div className="space-y-2">
                  <div className="flex justify-between py-1">
                    <span className="text-sm text-gray-600">Subtotal:</span>
                    <span className="text-sm font-medium text-gray-900">{formatCurrency(formData.subtotal)}</span>
                  </div>

                  {formData.discount > 0 && (
                    <div className="flex justify-between py-1">
                      <span className="text-sm text-gray-600">Discount ({formData.discount}%):</span>
                      <span className="text-sm font-medium text-gray-900">-{formatCurrency(discountAmount)}</span>
                    </div>
                  )}

                  {formData.invoiceType === 'with_gst' && Object.keys(gstBreakdown).length > 0 && (
                    <div className="border-t border-gray-200 pt-2">
                      <div className="text-xs font-medium text-gray-700 mb-1">GST Breakdown:</div>
                      {Object.entries(gstBreakdown).map(([rate, data]) => (
                        <div key={rate} className="space-y-1 mb-2">
                          <div className="flex justify-between py-1">
                            <span className="text-xs text-gray-600">CGST @ {rate / 2}%:</span>
                            <span className="text-xs font-medium text-gray-900">{formatCurrency(data.cgstAmount)}</span>
                          </div>
                          <div className="flex justify-between py-1">
                            <span className="text-xs text-gray-600">SGST @ {rate / 2}%:</span>
                            <span className="text-xs font-medium text-gray-900">{formatCurrency(data.sgstAmount)}</span>
                          </div>
                        </div>
                      ))}
                      <div className="flex justify-between py-1 border-t border-gray-200 mt-1 pt-1">
                        <span className="text-sm text-gray-600">Total GST:</span>
                        <span className="text-sm font-medium text-gray-900">{formatCurrency(formData.totalTaxAmount)}</span>
                      </div>
                    </div>
                  )}

                  <div className="border-t border-gray-200 mt-4 pt-4">
                    <div className="flex justify-between">
                      <span className="text-lg font-semibold text-gray-900">Total:</span>
                      <span className="text-lg font-bold text-gray-900">{formatCurrency(formData.totalAmount)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Notes and Terms */}
          {(formData.notes || formData.terms) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {formData.notes && (
                <div>
                  <h5 className="text-sm font-semibold text-gray-900 mb-2">Notes:</h5>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{formData.notes}</p>
                </div>
              )}

              {formData.terms && (
                <div>
                  <h5 className="text-sm font-semibold text-gray-900 mb-2">Terms & Conditions:</h5>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{formData.terms}</p>
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="border-t border-gray-200 pt-6">
            <div className="text-center text-sm text-gray-500">
              <p>Thank you for your business!</p>
              {formData.invoiceType === 'with_gst' && (
                <p className="mt-2">This is a computer generated invoice.</p>
              )}
            </div>
          </div>
        </div>

        {/* Preview Actions */}
        <div className="flex justify-end space-x-3 mt-6 no-print">
          <button
            type="button"
            onClick={() => setStep(1)}
            disabled={isSubmitting}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            Edit Invoice
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 flex items-center"
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating...
              </>
            ) : (
              <>
                <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Create
              </>
            )}
          </button>
        </div>
      </div>
    );

    if (isModal) {
      return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-4 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white mb-8">
            {content}
          </div>
        </div>
      );
    }

    return <div className="p-6">{content}</div>;
  }

  // Form Step (Step 1)
  const content = (
    <div className="bg-white">
      {/* Header Actions */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Create Sales Invoice</h3>

        </div>
        {isModal && (
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-300 rounded-md">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}


      {/* Mode Toggle - Add this after the header and before "Invoice Configuration" */}
      <div className="mb-6">
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-2xl border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-gray-900 font-semibold">Sale Mode</h4>
              <p className="text-gray-500 text-sm mt-1">
                {saleMode === 'offline' ? 'In-store sale with immediate payment' : 'Online order with delivery'}
              </p>
            </div>
            <div className="flex bg-white rounded-xl p-1 border border-gray-200 shadow-sm">
              <button
                type="button"
                onClick={() => setSaleMode('offline')}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${saleMode === 'offline'
                    ? 'bg-orange-600 text-white shadow-lg shadow-orange-600/30'
                    : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                🏪 Offline
              </button>
              <button
                type="button"
                onClick={() => setSaleMode('online')}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${saleMode === 'online'
                    ? 'bg-green-600 text-white shadow-lg shadow-green-600/30'
                    : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                🚚 Online Delivery
              </button>
            </div>
          </div>
        </div>
      </div>


      {saleMode === 'offline' ? (
        <form onSubmit={handleFormSubmit}>
          <div className="bg-white border border-gray-200 rounded-lg p-8">
            {/* Invoice Header */}
            <div className="flex justify-between items-start mb-8">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">INVOICE</h1>
                <div className="text-sm text-gray-600">
                  <p><strong>Invoice #:</strong> {invoiceNumber}</p>
                  <p><strong>Date:</strong> {formatDate(formData.invoiceDate)}</p>
                </div>
              </div>

              {formData.invoiceType === 'with_gst' && (
                <div className="text-right">
                  <h2 className="text-xl font-bold text-gray-900 mb-2">
                    {vendorProfile?.shopName || 'Your Shop Name'}
                  </h2>
                  <div className="text-sm text-gray-600">
                    <p>{vendorProfile?.address || 'Shop Address'}</p>
                    <p>{vendorProfile?.city || 'City'}, {vendorProfile?.state || 'State'} {vendorProfile?.pincode || 'PIN'}</p>
                    <p>Phone: {vendorProfile?.phone || 'N/A'}</p>
                    <p>Email: {vendorProfile?.email || 'N/A'}</p>
                    {vendorProfile?.gstNumber && (
                      <p>GST: {vendorProfile.gstNumber}</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Invoice Configuration */}
            <div className="mb-6 bg-blue-50 p-4 rounded-lg">
              <h4 className="text-md font-medium text-gray-900 mb-3">Invoice Configuration</h4>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Invoice Type</label>
                  <select
                    value={formData.invoiceType}
                    onChange={(e) => handleInputChange('invoiceType', e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                  >
                    <option value="with_gst">With GST</option>
                    <option value="without_gst">Without GST</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Bill To Section */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-semibold text-gray-900">Bill To:</h3>
                <button
                  type="button"
                  onClick={handleAddCustomer}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700"
                >
                  <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add Customer
                </button>
              </div>
              <div className="bg-gray-50 p-4 rounded-md border-2 border-dashed border-gray-300">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">Customer Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.customerName}
                      onChange={(e) => handleCustomerSearch(e.target.value)}
                      onFocus={() => {
                        if (formData.customerName.trim() && filteredCustomers.length > 0) {
                          setShowCustomerDropdown(true);
                        }
                      }}
                      onBlur={() => {
                        // Delay hiding dropdown to allow clicking on options
                        setTimeout(() => setShowCustomerDropdown(false), 200);
                      }}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                      placeholder="Search or enter customer name"
                    />

                    {/* Customer Dropdown */}
                    {showCustomerDropdown && filteredCustomers.length > 0 && (
                      <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                        {filteredCustomers.map((customer) => (
                          <div
                            key={customer.id}
                            className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-orange-50"
                            onClick={() => handleCustomerSelect(customer)}
                          >
                            <div className="flex items-center">
                              <span className="font-medium block truncate">
                                {customer.name}
                              </span>
                            </div>
                            <div className="text-gray-500 text-xs">
                              {customer.phone && <span>{customer.phone}</span>}
                              {customer.phone && customer.email && <span> • </span>}
                              {customer.email && <span>{customer.email}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <input
                      type="email"
                      value={formData.customerEmail}
                      onChange={(e) => handleInputChange('customerEmail', e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                      placeholder="customer@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Phone</label>
                    <input
                      type="tel"
                      value={formData.customerPhone}
                      onChange={(e) => handleInputChange('customerPhone', e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                      placeholder="+91 9876543210"
                    />
                  </div>
                  {formData.invoiceType === 'with_gst' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">GST Number *</label>
                      <input
                        type="text"
                        required={formData.invoiceType === 'with_gst'}
                        value={formData.customerGST}
                        onChange={(e) => handleInputChange('customerGST', e.target.value)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                        placeholder="Enter GST number"
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Invoice Date *</label>
                    <input
                      type="date"
                      required
                      value={formData.invoiceDate}
                      onChange={(e) => handleInputChange('invoiceDate', e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700">Address</label>
                  <textarea
                    rows={3}
                    value={formData.customerAddress}
                    onChange={(e) => handleInputChange('customerAddress', e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                    placeholder="Customer address"
                  />
                </div>
              </div>
            </div>

            {/* Items Table */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-lg font-semibold text-gray-900">Invoice Items</h4>
                <div className="flex space-x-2">
                  {products.length > 0 && (
                    <button
                      type="button"
                      onClick={addProductItem}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
                    >
                      <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Add Product
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={addManualItem}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add Manual Item
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 border border-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                        Description
                      </th>
                      <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                        HSN
                      </th>
                      <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                        Qty
                      </th>
                      <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                        Rate (₹)
                      </th>
                      {formData.invoiceType === 'with_gst' && (
                        <>
                          <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                            GST (%)
                          </th>
                          <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                            CGST (₹)
                          </th>
                          <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                            SGST (₹)
                          </th>
                        </>
                      )}
                      <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                        Amount (₹)
                      </th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                        Total (₹)
                      </th>
                      <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {formData.items.length === 0 ? (
                      <tr>
                        <td colSpan={formData.invoiceType === 'with_gst' ? "10" : "7"} className="px-6 py-8 text-center text-gray-500">
                          No items added yet. Click "Add Product" or "Add Manual Item" to get started.
                        </td>
                      </tr>
                    ) : (
                      formData.items.map((item, index) => {
                        const discountPercentage = parseFloat(formData.discount) || 0;
                        const itemDiscountAmount = (item.amount * discountPercentage) / 100;
                        const itemTaxableAmount = item.amount - itemDiscountAmount;
                        const { cgstAmount, sgstAmount } = formData.invoiceType === 'with_gst' ?
                          calculateCGSTSGST(itemTaxableAmount, item.gstRate || 0) :
                          { cgstAmount: 0, sgstAmount: 0 };
                        const totalWithGST = calculateItemTotalWithGST(item, discountPercentage);

                        return (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-3 py-4 border-r">
                              {item.isManual ? (
                                <input
                                  type="text"
                                  value={item.name}
                                  onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                                  placeholder="Enter item name"
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                                />
                              ) : (
                                <div className="space-y-2">
                                  <div className="flex items-center space-x-2">
                                    {item.productId ? (
                                      <div className="flex-1">
                                        <div className="text-sm font-medium text-gray-900">{item.name}</div>
                                        <div className="text-xs text-gray-500">ID: {item.productId}</div>
                                      </div>
                                    ) : (
                                      <div className="flex-1 text-gray-500 italic text-sm">No product</div>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => handleProductModalOpen()}
                                      className="px-2 py-1 border border-gray-300 rounded text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
                                    >
                                      {item.productId ? 'Change' : 'Select'}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </td>
                            <td className="px-2 py-4 text-center border-r">
                              <input
                                type="text"
                                value={item.hsnCode}
                                onChange={(e) => handleItemChange(index, 'hsnCode', e.target.value)}
                                placeholder="HSN"
                                className="w-16 px-1 py-1 border border-gray-300 rounded text-xs text-center focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                              />
                            </td>
                            <td className="px-2 py-4 text-center border-r">
                              <input
                                type="number"
                                min="1"
                                value={item.quantity}
                                onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                className="w-16 px-1 py-1 border border-gray-300 rounded text-xs text-center focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                              />
                            </td>
                            <td className="px-2 py-4 text-right border-r">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={item.rate}
                                onChange={(e) => handleItemChange(index, 'rate', e.target.value)}
                                disabled={!item.isManual && item.productId}
                                className="w-20 px-1 py-1 border border-gray-300 rounded text-xs text-right focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-100"
                              />
                            </td>
                            {formData.invoiceType === 'with_gst' && (
                              <>
                                <td className="px-2 py-4 text-center border-r relative">
                                  <div className="relative inline-block">
                                    <select
                                      value={item.gstRate || 0}
                                      onChange={(e) => handleItemChange(index, 'gstRate', e.target.value)}
                                      className="appearance-none w-20 px-2 py-1 pr-6 border border-gray-300 rounded text-xs text-center focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 bg-white cursor-pointer"
                                    >
                                      <option value={0}>0%</option>
                                      <option value={3}>3%</option>
                                      <option value={5}>5%</option>
                                      <option value={12}>12%</option>
                                      <option value={18}>18%</option>
                                      <option value={28}>28%</option>
                                    </select>
                                  </div>
                                </td>
                                <td className="px-2 py-4 text-right border-r">
                                  <span className="text-xs font-medium text-gray-900">
                                    {formatCurrency(cgstAmount)}
                                  </span>
                                </td>
                                <td className="px-2 py-4 text-right border-r">
                                  <span className="text-xs font-medium text-gray-900">
                                    {formatCurrency(sgstAmount)}
                                  </span>
                                </td>
                              </>
                            )}
                            <td className="px-2 py-4 text-right border-r">
                              <span className="text-sm font-medium text-gray-900">
                                {formatCurrency(item.amount)}
                              </span>
                            </td>
                            <td className="px-3 py-4 text-right border-r">
                              <span className="text-sm font-bold text-gray-900">
                                {formatCurrency(totalWithGST)}
                              </span>
                            </td>
                            <td className="px-2 py-4 text-center">
                              {formData.items.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removeItem(index)}
                                  className="text-red-600 hover:text-red-900"
                                  title="Remove item"
                                >
                                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Totals Section */}
            <div className="flex justify-end mb-8">
              <div className="w-96">
                <div className="bg-gray-50 p-6 rounded-md border">
                  <div className="space-y-2">
                    <div className="flex justify-between py-1">
                      <span className="text-sm text-gray-600">Subtotal:</span>
                      <span className="text-sm font-medium text-gray-900">{formatCurrency(formData.subtotal)}</span>
                    </div>

                    <div className="flex justify-between py-1">
                      <span className="text-sm text-gray-600">Discount (%):</span>
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={formData.discount}
                          onChange={(e) => handleInputChange('discount', e.target.value)}
                          className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-right focus:outline-none focus:ring-1 focus:ring-orange-500"
                          placeholder="0"
                        />
                        <span className="text-sm text-gray-600">%</span>
                      </div>
                    </div>

                    {formData.discount > 0 && (
                      <div className="flex justify-between py-1">
                        <span className="text-sm text-gray-600">Discount ({formData.discount}%):</span>
                        <span className="text-sm font-medium text-gray-900">-{formatCurrency((formData.subtotal * formData.discount) / 100)}</span>
                      </div>
                    )}

                    {formData.invoiceType === 'with_gst' && (
                      <div className="flex justify-between py-1">
                        <span className="text-sm text-gray-600">Total GST:</span>
                        <span className="text-sm font-medium text-gray-900">{formatCurrency(formData.totalTaxAmount)}</span>
                      </div>
                    )}

                    <div className="border-t border-gray-200 mt-4 pt-4">
                      <div className="flex justify-between">
                        <span className="text-lg font-semibold text-gray-900">Total:</span>
                        <span className="text-lg font-bold text-gray-900">{formatCurrency(formData.totalAmount)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Notes and Terms */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">Notes:</label>
                <textarea
                  rows={4}
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  placeholder="Any additional notes..."
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">Terms & Conditions:</label>
                <textarea
                  rows={4}
                  value={formData.terms}
                  onChange={(e) => handleInputChange('terms', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 pt-6">
              <div className="text-center text-sm text-gray-500">
                <p>Thank you for your business!</p>
                {formData.invoiceType === 'with_gst' && (
                  <p className="mt-2">This is a computer generated invoice.</p>
                )}
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!formData.customerName.trim() || formData.totalAmount <= 0}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
            >
              Preview Invoice
            </button>
          </div>
        </form>
      ) : (
        // ONLINE MODE UI
        <div className="space-y-6">
          {/* Order Details */}
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">📋 Order Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">Invoice Number</label>
                <input type="text" value={invoiceNumber} readOnly className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-900" />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">Order Date</label>
                <input type="date" value={formData.invoiceDate} onChange={(e) => handleInputChange('invoiceDate', e.target.value)} className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl" />
              </div>
            </div>
          </div>

          {/* Products & Pickup Locations - NEW FLOW */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">📦 Products & Pickup Locations</h3>
              <div className="flex items-center gap-2">
                {selectedProducts.length > 0 && (
                  <button
                    type="button"
                    onClick={() => { setProductModalMode('edit'); setShowOnlineProductModal(true); }}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-xl"
                  >
                    ✏️ Edit Products
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => { setProductModalMode('add'); setShowOnlineProductModal(true); }}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-xl"
                >
                  + Add Products
                </button>
              </div>
            </div>

            {/* Grouped Pickup Display */}
            {groupedPickups.length > 0 ? (
              <div className="space-y-4">
                {groupedPickups.map((pickup, pickupIndex) => (
                  <div key={pickup.supplierId} className="bg-white border-2 border-green-200 rounded-2xl overflow-hidden shadow-sm">
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-600 rounded-xl flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <h4 className="text-gray-900 font-semibold">{pickup.supplierName}</h4>
                          <p className="text-gray-500 text-sm">{pickup.supplierPhone} • {pickup.supplierAddress?.substring(0, 40) || 'No address'}...</p>
                        </div>
                        <div className="text-right">
                          <p className="text-gray-500 text-xs">{pickup.products.length} product(s)</p>
                          <p className="text-green-600 font-bold">{formatCurrency(pickup.products.reduce((s, p) => s + (p.price * p.quantity), 0))}</p>
                        </div>
                      </div>
                    </div>

                    <div className="p-4 space-y-2">
                      {pickup.products.map((product, productIndex) => (
                        <div key={product.uniqueKey || `${product.id}_${productIndex}`} className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg">
                          <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                            {product.imageUrl ? (
                              <img src={product.imageUrl} alt={product.name} className="w-full h-full object-cover" />
                            ) : (
                              <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                              </svg>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-gray-900 font-medium text-sm truncate">{product.name}</p>
                            <p className="text-gray-500 text-xs">{formatCurrency(product.price)} × {product.quantity}</p>
                          </div>
                          <div className="flex items-center gap-1">
                            <button type="button" onClick={() => updateGroupedProductQuantity(pickupIndex, productIndex, product.quantity - 1)} className="w-6 h-6 bg-gray-200 hover:bg-gray-300 rounded text-sm font-bold">-</button>
                            <span className="w-8 text-center text-sm font-medium">{product.quantity}</span>
                            <button type="button" onClick={() => updateGroupedProductQuantity(pickupIndex, productIndex, product.quantity + 1)} className="w-6 h-6 bg-gray-200 hover:bg-gray-300 rounded text-sm font-bold">+</button>
                          </div>
                          <span className="text-green-600 font-bold text-sm min-w-[60px] text-right">{formatCurrency(product.price * product.quantity)}</span>
                          <button type="button" onClick={() => removeProductFromGroupedPickup(pickupIndex, productIndex)} className="p-1 text-red-500 hover:bg-red-100 rounded">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {/* Summary */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-6">
                      <div className="text-center">
                        <p className="text-gray-500 text-sm">Pickups</p>
                        <p className="text-2xl font-bold text-gray-900">{groupedPickups.length}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-gray-500 text-sm">Products</p>
                        <p className="text-2xl font-bold text-gray-900">{newOnlineTotalProducts}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-gray-500 text-sm">Items</p>
                        <p className="text-2xl font-bold text-gray-900">{newOnlineTotalItems}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-gray-500 text-sm">Total Amount</p>
                      <p className="text-3xl font-bold text-green-600">{formatCurrency(newOnlineTotalAmount)}</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-2xl p-8 text-center">
                <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
                <p className="text-gray-500 mb-3">No products selected</p>
                <button
                  type="button"
                  onClick={() => { setProductModalMode('add'); setShowOnlineProductModal(true); }}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-xl"
                >
                  + Add Products & Assign Suppliers
                </button>
              </div>
            )}
          </div>

          {/* Delivery Location (Customer) - with search */}
          <div className="bg-white border-2 border-red-200 rounded-2xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Delivery Location (Customer)
              </h3>
              <button
                type="button"
                onClick={() => setShowCustomerModal(true)}
                className="flex items-center gap-1 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add Customer
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Customer Name with Search */}
              <div className="relative">
                <label className="block text-gray-700 text-sm font-medium mb-2">Customer Name *</label>
                <input
                  type="text"
                  value={formData.customerName}
                  onChange={(e) => handleDeliveryCustomerSearch(e.target.value)}
                  onFocus={() => {
                    if (formData.customerName?.trim() && filteredDeliveryCustomers.length > 0) {
                      setShowDeliveryCustomerDropdown(true);
                    }
                  }}
                  onBlur={() => setTimeout(() => setShowDeliveryCustomerDropdown(false), 200)}
                  placeholder="Search or enter customer name"
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500"
                />

                {/* Customer Dropdown */}
                {showDeliveryCustomerDropdown && filteredDeliveryCustomers.length > 0 && (
                  <div className="absolute z-20 mt-1 w-full bg-white shadow-lg max-h-48 rounded-xl border border-gray-200 overflow-auto">
                    {filteredDeliveryCustomers.map((customer) => (
                      <div
                        key={customer.id}
                        className="cursor-pointer py-3 px-4 hover:bg-red-50 border-b border-gray-100 last:border-0"
                        onClick={() => handleDeliveryCustomerSelect(customer)}
                      >
                        <p className="font-medium text-gray-900">{customer.name}</p>
                        <p className="text-gray-500 text-sm">
                          {customer.phone && <span>{customer.phone}</span>}
                          {customer.email && <span className="text-xs"> • {customer.email}</span>}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">Phone Number *</label>
                <input
                  type="tel"
                  value={formData.customerPhone}
                  onChange={(e) => handleInputChange('customerPhone', e.target.value)}
                  placeholder="+91 98765 43210"
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-gray-700 text-sm font-medium mb-2">Email (Optional)</label>
                <input
                  type="email"
                  value={formData.customerEmail}
                  onChange={(e) => handleInputChange('customerEmail', e.target.value)}
                  placeholder="customer@example.com"
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-gray-700 text-sm font-medium mb-2">Delivery Address *</label>
              <textarea
                value={formData.customerAddress}
                onChange={(e) => handleInputChange('customerAddress', e.target.value)}
                placeholder="Full delivery address"
                rows={2}
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">Landmark</label>
                <input
                  type="text"
                  value={formData.deliveryLandmark}
                  onChange={(e) => handleInputChange('deliveryLandmark', e.target.value)}
                  placeholder="Near..."
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">Latitude</label>
                <input
                  type="text"
                  value={formData.deliveryLatitude}
                  onChange={(e) => handleInputChange('deliveryLatitude', e.target.value)}
                  placeholder="e.g., 12.9716"
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">Longitude</label>
                <input
                  type="text"
                  value={formData.deliveryLongitude}
                  onChange={(e) => handleInputChange('deliveryLongitude', e.target.value)}
                  placeholder="e.g., 77.5946"
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
            </div>
          </div>

          {/* Payment & Porter Assignment */}
         {/* Payment & Porter Assignment */}
<div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
  <h3 className="text-lg font-semibold text-gray-900 mb-4">💳 Payment & Assignment</h3>
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
    <div>
      <label className="block text-gray-700 text-sm font-medium mb-2">Payment Mode</label>
      <div className="flex gap-2">
        {['cash', 'online', 'prepaid'].map(mode => (
          <button 
            key={mode} 
            type="button" 
            onClick={() => setFormData(prev => ({ ...prev, paymentMode: mode }))}
            className={`flex-1 py-3 rounded-xl font-medium transition-all ${
              formData.paymentMode === mode 
                ? (mode === 'cash' ? 'bg-yellow-100 text-yellow-800 border-2 border-yellow-400' 
                  : mode === 'online' ? 'bg-blue-100 text-blue-800 border-2 border-blue-400' 
                  : 'bg-green-100 text-green-800 border-2 border-green-400') 
                : 'bg-gray-100 text-gray-700 border-2 border-transparent'
            }`}
          >
            {mode === 'cash' && '💵 '}{mode === 'online' && '💳 '}{mode === 'prepaid' && '✅ '}
            {mode.charAt(0).toUpperCase() + mode.slice(1)}
          </button>
        ))}
      </div>
    </div>
    <div>
      <label className="block text-gray-700 text-sm font-medium mb-2">Amount</label>
      <input 
        type="text" 
        value={formatCurrency(newOnlineTotalAmount)} 
        readOnly 
        className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl font-bold" 
      />
    </div>
  </div>
  
  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
    {/* FIX: Porter Selection */}
    <div>
      <label className="block text-gray-700 text-sm font-medium mb-2">Select Porter</label>
      <select 
        value={formData.porterId || ''} 
        onChange={(e) => {
          console.log('Porter selected:', e.target.value);
          setFormData(prev => ({ ...prev, porterId: e.target.value }));
        }} 
        className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
      >
        <option value="">-- Unassigned --</option>
        {porters.filter(p => p.isOnline).map(porter => (
          <option key={porter.id} value={porter.id}>🟢 {porter.name}</option>
        ))}
        {porters.filter(p => !p.isOnline).map(porter => (
          <option key={porter.id} value={porter.id}>⚫ {porter.name}</option>
        ))}
      </select>
      {formData.porterId && (
        <p className="text-xs text-green-600 mt-1">
          ✓ Porter selected: {porters.find(p => p.id === formData.porterId)?.name}
        </p>
      )}
    </div>
    
    <div>
      <label className="block text-gray-700 text-sm font-medium mb-2">Priority</label>
      <select 
        value={formData.priority || 'normal'} 
        onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))} 
        className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
      >
        <option value="normal">🔵 Normal</option>
        <option value="high">🟠 High</option>
        <option value="urgent">🔴 Urgent</option>
      </select>
    </div>
    
    <div>
      <label className="block text-gray-700 text-sm font-medium mb-2">Estimated Time</label>
      <select 
        value={formData.estimatedTime || '45 mins'} 
        onChange={(e) => setFormData(prev => ({ ...prev, estimatedTime: e.target.value }))} 
        className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
      >
        <option value="30 mins">30 mins</option>
        <option value="45 mins">45 mins</option>
        <option value="1 hour">1 hour</option>
        <option value="2 hours">2 hours</option>
      </select>
    </div>
  </div>
</div>

          {/* Submit */}
          <div className="flex items-center gap-4">
            <button type="button" onClick={onClose} className="flex-1 py-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl">Cancel</button>
            <button
              type="button"
              onClick={handleOnlineSubmitNew}
              disabled={isSubmitting || selectedProducts.length === 0 || !formData.customerName || !formData.customerPhone || !formData.customerAddress}
              className="flex-1 py-4 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold rounded-xl disabled:opacity-50"
            >
              {isSubmitting ? 'Creating...' : `🚚 Create Delivery Task (${groupedPickups.length} Pickup${groupedPickups.length !== 1 ? 's' : ''})`}
            </button>
          </div>
        </div>
      )}

      {/* Product Selection Modal */}
      {/* Product Selection Modal */}
      <ProductSelectionModal
        isOpen={showProductModal}
        onClose={() => setShowProductModal(false)}
        products={products}
        onSelectProducts={saleMode === 'offline' ? handleProductSelect : handleOnlineProductsSelected}
        selectedProducts={saleMode === 'offline'
          ? formData.items.filter(item => item.productId)
          : (formData.pickups[activePickupIndex]?.products || []).map(p => ({
            productId: p.id || p.productId,
            name: p.name,
            quantity: p.quantity || 1,
            rate: p.rate || p.price || 0,
            hsnCode: p.hsnCode || p.partNumber || '',
            gstRate: p.gstRate || 18
          }))
        }
        onAddManualItem={saleMode === 'offline' ? addManualItem : () => setShowProductModal(false)}
        onNavigateToAddProduct={handleNavigateToAddProduct}
        brands={[]}
      />

      {/* Add SupplierModal at the bottom */}
      <SupplierModal
        isOpen={showSupplierModal}
        onClose={() => setShowSupplierModal(false)}
        onSave={handleSupplierSaved}
        vendorId={vendorProfile?.uid}
      />

      {/* Customer Modal */}
      <CustomerModal
        isOpen={showCustomerModal}
        onClose={() => setShowCustomerModal(false)}
        onSave={handleCustomerSaved}
      />


        {/* Online Product Selection Modal - NEW */}
<OnlineProductSelectionModal
  isOpen={showOnlineProductModal}
  onClose={() => setShowOnlineProductModal(false)}
  onSelect={handleNewProductsSelected}
  selectedProducts={selectedProducts}
  products={products}
  suppliers={suppliers}
  onSupplierAdded={handleNewSupplierAdded}
  mode={productModalMode}
  vendorId={vendorProfile?.uid}
/>
    </div>
  );

  // Return appropriate content based on modal state
  if (isModal) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-4 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white mb-8">
          {content}
        </div>
      </div>
    );
  }





  return <div className="p-6">{content}</div>;
};

export default CreateInvoiceModal